import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './App.css';
// Standalone mark for compact/dark chrome; orange SVG wordmark stays visible on #181818.
// (PNG combo wordmark uses navy text that disappears on the dark topbar.)
import brandLogo from './assets/madras-checks-logo.png';
import brandWordmark from './assets/madras-checks-wordmark.svg';
// import * as UTIF from 'utif'; // Removed due to compatibility issues
import { 
  Heart, 
  Trash2, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Maximize2, 
  Minimize2, 
  BookOpen, 
  Settings,
  Dice6,
  Palette,
  Sparkles,
  Shuffle,
  Loader2
} from 'lucide-react';
import {
  encodeConfigToPermalinkId,
  decodeGenerationId,
  buildSharePermalink,
  buildLocalPermalinkPath,
  parseGenerationPath,
} from './permalinkCodec.ts';
import {
  RANDOM_MADRAS,
  RANDOM_PALETTE,
  isSeededPaletteMode,
  listNamedPalettes,
  listNamedPalettesFromGroups,
  pickInitialPagePalette,
  resolveGenerationPalette,
  reusablePaletteColors,
} from './paletteSelection.ts';
import { createRandomMadrasPalette } from './randomMadrasPalette.ts';
import { createRandomHarmonyPalette } from './randomHarmonyPalette.ts';
import { COLOR_LIMIT_MAX, COLOR_LIMIT_MIN, normalizeColorLimit } from './colorLimit.ts';
import { buildArtworkPostText } from './artworkPost.ts';
import * as ExportReuse from './exportReuse.ts';
import {
  abandonPreviewBlobUrl,
  createPreviewBlobUrl,
  evictPreviewCacheEntriesForUrls,
  releaseArtsPreviewUrls,
  retainPreviewBlobUrl,
} from './previewBlobLifecycle.ts';
import {
  createPreviewScheduler,
  type ArtworkPreviewJob,
  type PreviewSchedulerHandlers,
} from './previewScheduler.ts';
import {
  GALLERY_CARD_GAP,
  GALLERY_CARD_SIZE,
  VirtualizedGalleryStrip,
} from './VirtualizedGalleryStrip.tsx';
import {
  abortGenerationJob,
  beginGenerationAbort,
  isAbortError,
  isGenerationEligible,
  markGenerationsCancelled,
  releaseGenerationAbort,
} from './generationAbortRegistry.ts';
import {
  getSessionPreviewCacheEntry,
  loadFavoritesFromLocalStorage,
  purgeLegacyPreviewIndexedDb,
  saveFavoritesToLocalStorage,
  setSessionPreviewCacheEntry,
} from './previewPersistence.ts';
import {
  attachPreviewToArtQueue,
  countPreviewProgress,
  filterReadyPreviewGalleryItems,
  formatPreviewRenderingStatus,
  indexOfArtId,
  isPreviewImageReady,
  markPreviewFailedInArtQueue,
  pickDisplayedArt,
  resolveFollowSelectionIndex,
  resolveIndexAfterDeletion,
  shiftIndexAfterPrepend,
  shouldReconcileFollowOnPreviewReady,
} from './previewContinuity.ts';

// TypeScript: declare window._env_ for custom env injection
declare global {
  interface Window {
    _env_?: { BACKEND_URL?: string };
  }
}

const MADRAS_PALETTES = [
  'Classic Red-Green',
  'Vintage Tamil 04',
  'Sunset Pondicherry',
  'Chennai Monsoon',
  'Kanchipuram Gold',
  'Natural Dyes',
  'Expanded Traditional',
  'Bleeding Vintage',
  'Warm Tamil Madras',
  'Ivy League',
  'Yale Blue',
  'Harvard Crimson',
  'Oxford Tartan',
  'Black Watch',
  'Royal Stewart',
  'Cornell Red',
  'Princeton Orange',
  'Dartmouth Green',
  'Calcutta Classic',
  'Scottish Highland',
  'French Riviera',
  'Tokyo Metro',
  'Cape Town Pastel',
  'Black & Red',
  'Indian Flag',
];

const PALETTE_GROUPS = [
  {
    label: 'CMYK Madras',
    palettes: ['Natural Dyes', 'Expanded Traditional', 'Bleeding Vintage', 'Warm Tamil Madras']
  },
  {
    label: 'Classic',
    palettes: [
      'Classic Red-Green', 'Vintage Tamil 04', 'Sunset Pondicherry', 'Chennai Monsoon', 'Kanchipuram Gold',
      'Madras Summer', 'Pondy Pastel', 'Tamil Sunrise', 'Chettinad Spice', 'Kerala Onam', 'Bengal Indigo', 'Goa Beach', 'Sri Lankan Tea', 'African Madras', 'Mumbai Monsoon',
    ]
  },
  {
    label: 'Ivy League',
    palettes: ['Ivy League', 'Yale Blue', 'Harvard Crimson', 'Cornell Red', 'Princeton Orange', 'Dartmouth Green']
  },
  {
    label: 'Tartan/Global',
    palettes: ['Oxford Tartan', 'Black Watch', 'Royal Stewart', 'Scottish Highland']
  },
  {
    label: 'Modern',
    palettes: ['French Riviera', 'Tokyo Metro', 'Cape Town Pastel', 'Black & Red', 'Indian Flag']
  },
  {
    label: 'Random',
    palettes: ['Random Madras', 'Random Palette']
  }
];

// Skin color palette for background options
const SKIN_COLORS = [
  { name: 'Black', value: '#000000', label: 'Black' },
  { name: 'White', value: '#fff', label: 'White' },
  { name: 'Very Light', value: '#f9e4d2', label: 'Very Light' },
  { name: 'Light', value: '#f5cfa0', label: 'Light' },
  { name: 'Medium Light', value: '#eac086', label: 'Medium Light' },
  { name: 'Medium', value: '#d1a074', label: 'Medium' },
  { name: 'Medium Dark', value: '#a8755c', label: 'Medium Dark' },
  { name: 'Dark', value: '#7c4f35', label: 'Dark' },
  { name: 'Very Dark', value: '#4b2e1f', label: 'Very Dark' },
  { name: 'Transparent', value: 'transparent', label: 'Transparent' },
];

// Canvas size options for export
const CANVAS_SIZES = [
  { label: "2048 x 2048", value: 2048 },
  { label: "4096 x 4096", value: 4096 },
  { label: "4200 x 4200", value: 4200 },
  { label: "6900 x 6900", value: 6900 }
];

// Export format options
const EXPORT_FORMATS = [
  { label: "PNG", value: "png", description: "High quality, web optimized" },
  { label: "TIFF", value: "tiff", description: "Professional printing" },
  { label: "JPEG", value: "jpeg", description: "Smaller file size" },
  { label: "WebP", value: "webp", description: "Modern web format" }
];

// Helper to get palette colors by name
const PALETTE_COLORS: Record<string, string[]> = {
  // CMYK-Safe Madras Palettes
  'Natural Dyes': ['#405BAA', '#B33A3A', '#D9A43B', '#1F1E1D', '#5A7A5A'],
  'Expanded Traditional': ['#405BAA', '#B33A3A', '#D9A43B', '#5A7A5A', '#8C5832', '#A48E7F', '#1F1E1D', '#FAF1E3'],
  'Bleeding Vintage': ['#3A62B3', '#C13D3D', '#D9A43B', '#7DAC9B', '#D87BA1', '#7A4E8A', '#F2E4BE', '#1F1E1D', '#E0B64C', '#7E5E49'],
  'Warm Tamil Madras': ['#C13D3D', '#F5C03A', '#3E5F9A', '#88B0D3', '#ADC178', '#E77F37', '#FAF3EB', '#F2E4BE', '#7E5E49', '#1D1C1A'],
  
  // Original Palettes
  'Classic Red-Green': ['#cc0033', '#ffee88', '#004477', '#ffffff'],
  'Vintage Tamil 04': ['#e63946', '#f1faee', '#a8dadc', '#457b9d'],
  'Sunset Pondicherry': ['#ffb347', '#ff6961', '#6a0572', '#fff8e7'],
  'Chennai Monsoon': ['#1d3557', '#457b9d', '#a8dadc', '#f1faee'],
  'Kanchipuram Gold': ['#ffd700', '#b8860b', '#8b0000', '#fff8e7'],
  'Madras Summer': ['#f7c873', '#e94f37', '#393e41', '#3f88c5', '#fff8e7'],
  'Pondy Pastel': ['#f7cac9', '#92a8d1', '#034f84', '#f7786b', '#fff8e7'],
  'Tamil Sunrise': ['#ffb347', '#ff6961', '#fff8e7', '#1d3557', '#e63946'],
  'Chettinad Spice': ['#d72631', '#a2d5c6', '#077b8a', '#5c3c92', '#f4f4f4'],
  'Kerala Onam': ['#fff8e7', '#ffd700', '#e94f37', '#393e41', '#3f88c5'],
  'Bengal Indigo': ['#1a2634', '#3f88c5', '#f7c873', '#e94f37', '#fff8e7'],
  'Goa Beach': ['#f7cac9', '#f7786b', '#034f84', '#fff8e7', '#393e41'],
  'Sri Lankan Tea': ['#a8dadc', '#457b9d', '#e63946', '#f1faee', '#fff8e7'],
  'African Madras': ['#ffb347', '#e94f37', '#393e41', '#3f88c5', '#ffd700'],
  'Mumbai Monsoon': ['#1d3557', '#457b9d', '#a8dadc', '#f1faee', '#ffd700'],
  'Ivy League': ['#002147', '#a6192e', '#f4f4f4', '#ffd700', '#005a9c'],
  'Yale Blue': ['#00356b', '#ffffff', '#c4d8e2', '#8c1515'],
  'Harvard Crimson': ['#a51c30', '#ffffff', '#000000', '#b7a57a'],
  'Cornell Red': ['#b31b1b', '#ffffff', '#222222', '#e5e5e5'],
  'Princeton Orange': ['#ff8f1c', '#000000', '#ffffff', '#e5e5e5'],
  'Dartmouth Green': ['#00693e', '#ffffff', '#000000', '#a3c1ad'],
  // Indian national flag colors: saffron, white, green, navy blue (Ashoka Chakra)
  'Indian Flag': ['#ff9933', '#ffffff', '#138808', '#000080'],
  'Oxford Tartan': ['#002147', '#c8102e', '#ffd700', '#ffffff', '#008272'],
  'Black Watch': ['#1c2a3a', '#2e4a62', '#1e2d24', '#3a5f0b'],
  'Royal Stewart': ['#e10600', '#ffffff', '#000000', '#ffd700', '#007a3d'],
  'Scottish Highland': ['#005eb8', '#ffd700', '#e10600', '#ffffff', '#000000'],
  'French Riviera': ['#0055a4', '#ffffff', '#ef4135', '#f7c873'],
  'Tokyo Metro': ['#e60012', '#0089a7', '#f6aa00', '#ffffff'],
  'Cape Town Pastel': ['#f7cac9', '#92a8d1', '#034f84', '#f7786b'],
  'Black & Red': ['#000000', '#cc0033'],
};

// Random Madras swatch pool and its seeded generator live in randomMadrasPalette.ts
// so a palette can be reproduced from the artwork seed.

function formatHexColor(color: string): string {
  const raw = String(color || '').trim();
  if (!raw) return '';
  let hex = raw.startsWith('#') ? raw.slice(1) : raw;
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return `#${hex.toUpperCase()}`;
  }
  return raw;
}

function PaletteSwatch({
  colors,
  showHexTooltips = false,
}: {
  colors: string[];
  showHexTooltips?: boolean;
}) {
  return (
    <span className="EOL-palette-swatch" style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {colors.map((c, i) => {
        // Tooltip uses the same colour value that paints the swatch.
        const hex = formatHexColor(c);
        if (!showHexTooltips) {
          return (
            <span
              key={`${c}-${i}`}
              className="EOL-palette-swatch-chip"
              style={{ background: c }}
            />
          );
        }
        return (
          <button
            key={`${c}-${i}`}
            type="button"
            className="EOL-palette-swatch-chip is-interactive"
            style={{ background: c }}
            data-hex={hex}
            aria-label={hex}
            onMouseEnter={(e) => e.currentTarget.classList.add('is-tooltip-open')}
            onMouseLeave={(e) => e.currentTarget.classList.remove('is-tooltip-open')}
            onFocus={(e) => e.currentTarget.classList.add('is-tooltip-open')}
            onBlur={(e) => e.currentTarget.classList.remove('is-tooltip-open')}
            onTouchStart={(e) => {
              const btn = e.currentTarget;
              const open = btn.classList.contains('is-tooltip-open');
              document
                .querySelectorAll('.EOL-palette-swatch-chip.is-tooltip-open')
                .forEach((node) => node.classList.remove('is-tooltip-open'));
              if (!open) btn.classList.add('is-tooltip-open');
            }}
          />
        );
      })}
    </span>
  );
}

// Display helpers for art preview Specs (do not affect generation)
function getDisplayPaletteName(config: any): string {
  if (typeof config?.paletteName === 'string' && config.paletteName) {
    return config.paletteName;
  }
  if (typeof config?.palette === 'string' && config.palette) {
    return config.palette;
  }
  return 'Custom';
}

function getDisplayPaletteColors(config: any): string[] {
  if (Array.isArray(config?.palette)) {
    return config.palette;
  }
  if (typeof config?.palette === 'string' && PALETTE_COLORS[config.palette]) {
    return PALETTE_COLORS[config.palette];
  }
  if (Array.isArray(config?.paletteColors)) {
    return config.paletteColors;
  }
  return [];
}

function formatThreadSpacing(value: any): string {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return value == null ? '' : String(value);
  }
  return String(Math.round(n * 100) / 100);
}

  // Normalize config for API: ensure palette is an array of hex strings
  function normalizeConfigForAPI(cfg: any) {
    let paletteToSend: string[];
    if (Array.isArray(cfg.palette)) {
      paletteToSend = cfg.palette as string[];
    } else if (cfg.palette === RANDOM_MADRAS) {
      // Prefer colors attached to this generation; otherwise derive them from the seed.
      paletteToSend =
        reusablePaletteColors(cfg.paletteColors) || createRandomMadrasPalette(cfg.seed);
    } else if (cfg.palette === RANDOM_PALETTE) {
      paletteToSend =
        reusablePaletteColors(cfg.paletteColors) ||
        createRandomHarmonyPalette({
          seed: cfg.seed,
          colorLimitMode: cfg.colorLimitMode,
          colorLimit: cfg.colorLimit,
        });
    } else if (typeof cfg.palette === 'string' && !isSeededPaletteMode(cfg.palette)) {
      paletteToSend = PALETTE_COLORS[cfg.palette] || ['#cc0033', '#ffee88', '#004477', '#ffffff'];
    } else {
      paletteToSend = ['#cc0033', '#ffee88', '#004477', '#ffffff'];
    }

    // Keep the generator's canonical palette name for Specs display.
    // Do not infer names from hex colors.
    const paletteName =
      typeof cfg.paletteName === 'string' && cfg.paletteName
        ? cfg.paletteName
        : typeof cfg.palette === 'string' && cfg.palette
          ? cfg.palette
          : undefined;
    
    // FIXED: Validate and ensure clothFade is always a proper boolean
    const validatedConfig = { 
      ...cfg, 
      palette: paletteToSend,
      ...(paletteName ? { paletteName } : {}),
      ...(isSeededPaletteMode(paletteName) ? { paletteColors: paletteToSend } : {}),
      colorLimit: normalizeColorLimit(cfg.colorLimit, COLOR_LIMIT_MIN),
      clothFade: Boolean(cfg.clothFade), // Ensure it's always a boolean
      clothFlow: Boolean(cfg.clothFlow), // Ensure it's always a boolean
      diagonalMode: Boolean(cfg.diagonalMode) // Ensure it's always a boolean
    };
    
    // DEBUG: Log validation for troubleshooting
    if (validatedConfig.diagonalMode) {
      console.log('Config validation:', {
        clothFade: validatedConfig.clothFade,
        clothFlow: validatedConfig.clothFlow,
        diagonalMode: validatedConfig.diagonalMode,
        patternType: validatedConfig.patternType
      });
    }
    
    return validatedConfig;
  }

  // --- Preview cache (in-memory, session-only) ---
  // Values are blob: object URLs owned by artwork ids via Phase 4A previewBlobLifecycle.
  // Phase 4E: never persist these to IndexedDB/localStorage (blob: dies on reload/revoke).
  const previewCache: Map<string, string> = new Map();

  type ArtPreviewFields = {
    id: string;
    previewUrl?: string;
    displayUrl?: string;
  };

  function cleanupArtsPreviewResources(arts: ArtPreviewFields[]): void {
    if (!arts.length) return;
    const urls: Array<string | undefined> = [];
    for (const art of arts) {
      urls.push(art.previewUrl, art.displayUrl);
    }
    evictPreviewCacheEntriesForUrls(previewCache, urls);
    releaseArtsPreviewUrls(arts);
  }

  function computePreviewKey(cfg: any): string {
    try {
      const keyObj = {
        seed: cfg?.seed,
        patternType: cfg?.patternType,
        palette: Array.isArray(cfg?.palette) ? cfg.palette : String(cfg?.palette || ''),
        threadSpacing: cfg?.threadSpacing,
        bgColor: cfg?.bgColor,
        lint: !!cfg?.lint,
        diagonalMode: !!cfg?.diagonalMode,
        clothFlow: !!cfg?.clothFlow,
        clothFade: !!cfg?.clothFade,
      };
      return JSON.stringify(keyObj);
    } catch {
      return Math.random().toString(36).slice(2);
    }
  }

  // --- Preview PNG queue (single concurrency) ---
  // Prefer OffscreenCanvas Worker for ALL modes when feature-detected (including Cloth Flow).
  // Cloth Flow fold angle uses Math.random (not seed-stable); Worker vs iframe pixels may differ
  // between independent runs — same as two iframe runs today. Iframe remains fallback only.
  type WorkerJob = {
    id: string;
    script: string;
    width: number;
    height: number;
    allowOffscreenWorker: boolean;
    resolve: (url: string | null) => void;
  };

  let workerBusy = false;
  const workerQueue: WorkerJob[] = [];

  let offscreenPreviewWorker: Worker | null = null;
  let offscreenPreviewWorkerFailed = false;

  function isOffscreenPreviewWorkerSupported(): boolean {
    if (typeof Worker === 'undefined') return false;
    if (typeof OffscreenCanvas === 'undefined') return false;
    try {
      const probe = new OffscreenCanvas(1, 1);
      if (!probe.getContext('2d')) return false;
    } catch {
      return false;
    }
    return true;
  }

  function getOffscreenPreviewWorker(): Worker | null {
    if (offscreenPreviewWorkerFailed) return null;
    if (!isOffscreenPreviewWorkerSupported()) {
      offscreenPreviewWorkerFailed = true;
      return null;
    }
    if (offscreenPreviewWorker) return offscreenPreviewWorker;
    try {
      offscreenPreviewWorker = new Worker(new URL('./previewWorker.ts', import.meta.url));
      offscreenPreviewWorker.addEventListener('error', () => {
        offscreenPreviewWorkerFailed = true;
        try { offscreenPreviewWorker?.terminate(); } catch { /* ignore */ }
        offscreenPreviewWorker = null;
      });
      return offscreenPreviewWorker;
    } catch (error) {
      console.warn('Offscreen preview Worker unavailable, using iframe fallback:', error);
      offscreenPreviewWorkerFailed = true;
      return null;
    }
  }

  async function renderPreviewInOffscreenWorker(
    script: string,
    width: number,
    height: number,
    ownerId: string
  ): Promise<string | null> {
    const worker = getOffscreenPreviewWorker();
    if (!worker) return null;
    const id = `offscreen_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    return new Promise<string | null>((resolve) => {
      const timeoutMs = 180000;
      const timeout = window.setTimeout(() => {
        cleanup();
        console.warn('Offscreen preview Worker timed out; falling back to iframe');
        resolve(null);
      }, timeoutMs);

      const onMessage = (ev: MessageEvent) => {
        const data = ev.data as { type?: string; id?: string; ok?: boolean; error?: string; buffer?: ArrayBuffer };
        if (!data || data.type !== 'done' || data.id !== id) return;
        cleanup();
        if (data.ok && data.buffer) {
          const blob = new Blob([data.buffer], { type: 'image/png' });
          resolve(createPreviewBlobUrl(blob, ownerId));
        } else {
          console.warn('Offscreen preview Worker failed:', data.error || 'unknown error');
          resolve(null);
        }
      };

      const onError = () => {
        cleanup();
        offscreenPreviewWorkerFailed = true;
        resolve(null);
      };

      const cleanup = () => {
        window.clearTimeout(timeout);
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
      };

      worker.addEventListener('message', onMessage);
      worker.addEventListener('error', onError);
      worker.postMessage({ type: 'renderScript', script, id, width, height });
    });
  }

  async function processWorkerQueue() {
    if (workerBusy) return;
    const job = workerQueue.shift();
    if (!job) return;
    workerBusy = true;
    
    // Use requestIdleCallback to avoid blocking the main thread
    const scheduleJob = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => performJob(), { timeout: 3000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => performJob(), 0);
      }
    };
    
    const performJob = async () => {
      try {
        console.log('Processing PNG generation job in background...');
        const url = await renderPreviewPreferWorker(job.script, job.width, job.height, {
          allowOffscreenWorker: job.allowOffscreenWorker,
          ownerId: job.id,
        });
        job.resolve(url);
      } catch (error) {
        console.warn('PNG generation job failed:', error);
        job.resolve(null);
      } finally {
        workerBusy = false;
        // Schedule next job with a small delay to prevent blocking
        setTimeout(processWorkerQueue, 50);
      }
    };
    
    scheduleJob();
  }

  function enqueueWorkerRender(
    id: string,
    script: string,
    width: number,
    height: number,
    allowOffscreenWorker: boolean = true
  ) {
    return new Promise<string | null>((resolve) => {
      workerQueue.push({ id, script, width, height, allowOffscreenWorker, resolve });
      processWorkerQueue();
    });
  }

  // --- Phase 4B: preview pipeline (independent of API generation queue) ---
  // Handlers are filled from App each render via setPreviewSchedulerBridge.
  let previewSchedulerHandlers: PreviewSchedulerHandlers | null = null;

  function setPreviewSchedulerBridge(handlers: PreviewSchedulerHandlers): void {
    previewSchedulerHandlers = handlers;
  }

  const artworkPreviewScheduler = createPreviewScheduler(() => {
    if (!previewSchedulerHandlers) {
      throw new Error('Preview scheduler bridge not initialized');
    }
    return previewSchedulerHandlers;
  });

  function enqueueArtworkPreview(job: ArtworkPreviewJob): void {
    artworkPreviewScheduler.enqueue(job);
  }

  async function renderPreviewInWorker(
    script: string,
    width: number,
    height: number,
    ownerId: string
  ): Promise<string | null> {
    try {
      console.log('Starting non-blocking PNG generation...');
      
      return new Promise((resolve) => {
        // Use requestIdleCallback to avoid blocking the main thread
        const scheduleRender = () => {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => performRender(), { timeout: 5000 });
          } else {
            // Fallback for browsers without requestIdleCallback
            setTimeout(() => performRender(), 0);
          }
        };
        
        const performRender = async () => {
          try {
            // Create a temporary iframe but optimize it for PNG generation
            const tempIframe = document.createElement('iframe');
            tempIframe.style.position = 'absolute';
            tempIframe.style.left = '-9999px';
            tempIframe.style.width = `${width}px`;
            tempIframe.style.height = `${height}px`;
            tempIframe.style.border = 'none';
            tempIframe.style.transform = 'scale(1)';
            tempIframe.style.transformOrigin = 'top left';
            
            document.body.appendChild(tempIframe);
            
            // Create HTML with the script
            const html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0"><canvas id="c" width="${width}" height="${height}"></canvas><script>${script}</script></body></html>`;
            
            // Wait for iframe to load
            await new Promise(resolve => {
              tempIframe.onload = resolve;
              tempIframe.srcdoc = html;
            });
            
            // Get the canvas from the iframe
            const iframeDoc = tempIframe.contentDocument || tempIframe.contentWindow?.document;
            if (!iframeDoc) throw new Error('Could not access iframe document');
            
            const canvas = iframeDoc.querySelector('canvas');
            if (!canvas) throw new Error('No canvas found in HTML');
            
            // Wait for canvas to be ready (algorithm to render) - shorter wait for previews
            await new Promise(resolve => {
              window.setTimeout(resolve, 200);
            });
            
            // Convert canvas to blob with non-blocking approach
            const blob = await new Promise<Blob>((resolveBlob, rejectBlob) => {
              // Use a timeout to prevent infinite waiting
              const timeout = setTimeout(() => {
                rejectBlob(new Error('PNG generation timeout'));
              }, 10000);
              
              canvas.toBlob((blob) => {
                clearTimeout(timeout);
                if (blob) {
                  resolveBlob(blob);
                } else {
                  rejectBlob(new Error('Failed to generate PNG blob'));
                }
              }, 'image/png', 0.9);
            });
            
            const pngUrl = createPreviewBlobUrl(blob, ownerId);
            
            // Clean up immediately
            document.body.removeChild(tempIframe);
            
            console.log('PNG generation successful');
            resolve(pngUrl);
          } catch (error) {
            console.warn('PNG generation failed:', error);
            resolve(null);
          }
        };
        
        scheduleRender();
      });
    } catch (error) {
      console.warn('PNG generation failed, falling back to iframe:', error);
      return null;
    }
  }

  function guessCanvasIdFromScript(script: string): string {
    if (/getElementById\(["']c["']\)/.test(script)) return 'c';
    if (/getElementById\(["']canvas["']\)/.test(script)) return 'canvas';
    if (/getElementById\(["'][^"']+["']\)/.test(script)) {
      const m = script.match(/getElementById\(["']([^"']+)["']\)/);
      if (m && m[1]) return m[1];
    }
    return 'c';
  }

  async function renderPreviewWithIframe(script: string, width: number, height: number): Promise<string | null> {
    try {
      const targetId = guessCanvasIdFromScript(script);
      // Avoid no-useless-escape and no-useless-concat by composing via join
      const closing = ['</', 'script>'].join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0"><canvas id="c" width="${width}" height="${height}"></canvas><canvas id="canvas" width="${width}" height="${height}"></canvas><script>${script}${closing}</body></html>`;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.left = '-10000px';
      iframe.style.top = '-10000px';
      iframe.width = String(width);
      iframe.height = String(height);
      document.body.appendChild(iframe);
      iframe.srcdoc = html;
      await new Promise(r => setTimeout(r, 500));
      const doc = iframe.contentDocument || iframe.ownerDocument;
      const canvas = (doc?.getElementById(targetId) as HTMLCanvasElement) || (doc?.getElementById('canvas') as HTMLCanvasElement) || (doc?.getElementById('c') as HTMLCanvasElement);
      const url = canvas?.toDataURL ? canvas.toDataURL('image/png') : null;
      document.body.removeChild(iframe);
      return url;
    } catch {
      return null;
    }
  }

  async function renderPreviewPreferWorker(
    script: string,
    width: number,
    height: number,
    opts?: { allowOffscreenWorker?: boolean; ownerId?: string }
  ): Promise<string | null> {
    const ownerId = opts?.ownerId || `preview_${Date.now()}`;
    // Prefer real OffscreenCanvas Worker for every mode (Classic/Bleed/Modern/Diagonal/Cloth Flow).
    // On unsupported browsers or Worker failure, fall back to the hidden-iframe path.
    if (opts?.allowOffscreenWorker !== false) {
      const offscreenUrl = await renderPreviewInOffscreenWorker(script, width, height, ownerId);
      if (offscreenUrl) return offscreenUrl;
    }
    // Existing main-thread hidden-iframe path (name retained for minimal diff)
    const workerUrl = await renderPreviewInWorker(script, width, height, ownerId);
    if (workerUrl) return workerUrl;
    return await renderPreviewWithIframe(script, width, height);
  }

// Shared queue status UI — same state/handlers for desktop Generate card and mobile dock
function GenerateQueueStatus({
  pendingCount,
  currentJob,
  onCancelCurrent,
  onClearQueue,
  previewStatus,
}: {
  pendingCount: number;
  currentJob: { id?: string } | null;
  onCancelCurrent: () => void;
  onClearQueue: () => void;
  /** Phase 4F: status outside artwork (never overlays the canvas). */
  previewStatus?: string | null;
}) {
  return (
    <div className="EOL-generate-queue-status">
      <div className="EOL-generate-queue-status-row">
        <span>Pending jobs: {pendingCount}</span>
        {currentJob && (
          <span className="EOL-generate-queue-processing">• Processing</span>
        )}
      </div>
      <div
        className="EOL-generate-queue-status-row"
        style={{ minHeight: '1.5em', lineHeight: '1.5em' }}
        aria-live="polite"
      >
        {previewStatus ? (
          <span className="EOL-generate-queue-processing">{previewStatus}</span>
        ) : null}
      </div>
      <div className="EOL-generate-queue-status-actions">
        <button
          type="button"
          className={`EOL-generate-queue-btn is-cancel${currentJob ? ' is-enabled' : ''}`}
          onClick={onCancelCurrent}
          disabled={!currentJob}
        >
          Cancel Current
        </button>
        <button
          type="button"
          className={`EOL-generate-queue-btn is-clear${pendingCount > 0 ? ' is-enabled' : ''}`}
          onClick={onClearQueue}
          disabled={pendingCount === 0}
        >
          Clear Queue
        </button>
      </div>
    </div>
  );
}

// QQL-style custom palette picker
function PaletteDropdown({palette, setPalette, groups, getColors, paletteColors}: {
  palette: string | string[],
  setPalette: (p: string) => void,
  groups: { label: string, palettes: string[] }[],
  getColors: (name: string) => string[],
  paletteColors?: string[] | null
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  const paletteName = typeof palette === 'string' ? palette : RANDOM_MADRAS;
  // Do not mint seeded random colours on open/select — only at generation time.
  const seededPreview = ['#cc0033', '#ffee88', '#004477', '#ffffff'];
  const selectedColors =
    isSeededPaletteMode(paletteName)
      ? (paletteColors && paletteColors.length ? paletteColors : seededPreview)
      : (getColors(paletteName) || ['#ccc', '#eee', '#aaa', '#fff']);
  return (
    <div ref={ref} style={{position:'relative',marginBottom:8}}>
      <div
        className="EOL-palette-picker-closed"
        style={{display:'flex',alignItems:'center',cursor:'pointer',padding:'6px 12px',borderRadius:8,background:'#18191c',border:'1px solid #333',width:'100%',boxSizing:'border-box'}}
        onClick={()=>setOpen(v=>!v)}
      >
        <PaletteSwatch colors={selectedColors} />
        <span style={{fontWeight:500,fontSize:16,minWidth:0}}>{paletteName}</span>
        <span style={{marginLeft:'auto',fontSize:18,opacity:0.5,flexShrink:0}}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="EOL-palette-picker-dropdown" style={{position:'absolute',zIndex:60,top:'110%',left:0,right:0,background:'#18191c',border:'1px solid #333',borderRadius:10,boxShadow:'0 4px 24px #0008',padding:'8px 0',maxHeight:340,overflowY:'auto',boxSizing:'border-box'}}>
          {groups.map(group => (
            <div key={group.label} style={{padding:'4px 16px 2px',fontWeight:600,fontSize:15,color:'#fff',letterSpacing:0.5}}>{group.label}
              {group.palettes.map(p => (
                <div
                  key={p}
                  onClick={()=>{setPalette(p);setOpen(false);}}
                  style={{display:'flex',alignItems:'center',padding:'6px 0',cursor:'pointer',borderRadius:6,background:p===paletteName?'#222':'none',margin:'2px 0'}}
                >
                  <PaletteSwatch colors={isSeededPaletteMode(p) ? seededPreview : (getColors(p) || ['#ccc', '#eee', '#aaa', '#fff'])} />
                  <span style={{fontSize:15,color:p===paletteName?'#fff':'#eee',fontWeight:p===paletteName?600:400}}>{p}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Background color button picker
function BackgroundColorPicker({bgColor, setBgColor, opaque, setOpaque}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: 8,
      marginBottom: 8,
      maxWidth: '200px',
      margin: '0 auto 8px auto'
    }}>
      {SKIN_COLORS.map(color => (
        <button
          key={color.value}
          onClick={() => {
            setBgColor(color.value);
            if (color.value === 'transparent') {
              setOpaque(false);
            } else {
              setOpaque(true);
            }
          }}
          style={{
            width: '100%',
            aspectRatio: '1',
            background: color.value === 'transparent' 
              ? 'repeating-conic-gradient(from 0deg, #333 0deg 10deg, #666 10deg 20deg)' 
              : color.value,
            border: color.value === bgColor 
              ? '2px solid #EB420E' 
              : color.value === 'transparent' 
                ? '1px solid #333' 
                : '1px solid #222',
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: color.value === bgColor ? '0 0 8px rgba(247, 147, 26, 0.4)' : 'none'
          }}
          title={color.label}
        />
      ))}
    </div>
  );
}

export default function App() {
  // DEV: URL parameter handling for manual testing
  const urlParams = new URLSearchParams(window.location.search);
  const devSeed = urlParams.get('seed');
  const devPattern = urlParams.get('pattern') as 'classic' | 'bleed' | 'modern' | null;
  const devPalette = urlParams.get('palette');
  const devThreadSpacing = urlParams.get('threadSpacing');
  const devBgColor = urlParams.get('bgColor');
  const devLint = urlParams.get('lint');
  const devDiagonalMode = urlParams.get('diagonalMode');
  const devClothFlow = urlParams.get('clothFlow');
  const devClothFade = urlParams.get('clothFade');
  const devDensityPreset = urlParams.get('densityPreset') as 'random' | 'airy' | 'balanced' | 'tight' | null;
  const devColorLimitMode = urlParams.get('colorLimitMode') as 'off' | 'auto' | 'manual' | null;
  const devColorLimit = urlParams.get('colorLimit');
  
  // State for config
  const [seed, setSeed] = useState(() => {
    // Use URL parameter if available for development testing
    if (devSeed) return devSeed;
    return Array.from({length: 16}, () => Math.floor(Math.random()*16).toString(16)).join('');
  });
  const [palette, setPalette] = useState<string | string[]>(() => {
    if (devPalette) {
      if (devPalette.includes(',')) {
        return devPalette.split(',');
      }
      return devPalette;
    }
    // /g/{id} restores the exact palette in loadConfigFromURL — do not randomize over it.
    if (typeof window !== 'undefined' && parseGenerationPath(window.location.pathname)) {
      return MADRAS_PALETTES[0];
    }
    // Fresh page load: one-shot random named palette from the Palette dropdown (never Random Madras).
    const namedFromDropdown = listNamedPalettesFromGroups(PALETTE_GROUPS);
    return pickInitialPagePalette(namedFromDropdown, MADRAS_PALETTES[0]);
  });
  const [paletteColors, setPaletteColors] = useState<string[] | null>(null); // NEW: store actual colors if Random Madras
  const [lint, setLint] = useState(() => devLint ? devLint === 'true' : true);
  // Replace weave slider with 4 buttons driving an abstract density value
  const [threadSpacing, setThreadSpacing] = useState(() => devThreadSpacing ? parseInt(devThreadSpacing) : 5);
  const [densityPreset, setDensityPreset] = useState<'random'|'airy'|'balanced'|'tight'>(() => {
    if (devDensityPreset && ['random', 'airy', 'balanced', 'tight'].includes(devDensityPreset)) {
      return devDensityPreset;
    }
    return 'balanced';
  });

  const [gallery, setGallery] = useState<any[]>(() => loadFavoritesFromLocalStorage());

  // Phase 4E: drop legacy IDB that stored dead blob: URL strings.
  useEffect(() => {
    void purgeLegacyPreviewIndexedDb();
  }, []);
  
  // Gallery selection and management state
  const [selectedGalleryItems, setSelectedGalleryItems] = useState<Set<string>>(new Set());
  const [galleryView, setGalleryView] = useState<'queue' | 'favorites' | 'listed'>('queue');
  const [listedItems, setListedItems] = useState<Set<string>>(new Set());
  const [bgColor, setBgColor] = useState(() => devBgColor || '#fff');
  const [opaque, setOpaque] = useState(true);


  // Random toggle states for each variation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [randomSeed, setRandomSeed] = useState(false);
  const [randomPaletteToggle, setRandomPaletteToggle] = useState(false);
  const [randomBgColor, setRandomBgColor] = useState(false);
  const [randomLint, setRandomLint] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [randomAll, setRandomAll] = useState(false);

  // Pattern and style states
  const [patternType, setPatternType] = useState<'classic' | 'bleed' | 'modern'>(() => {
    // Use URL parameter if available for development testing
    if (devPattern && ['classic', 'bleed', 'modern'].includes(devPattern)) {
      return devPattern;
    }
    return 'classic';
  });
  const [diagonalMode, setDiagonalMode] = useState(() => devDiagonalMode ? devDiagonalMode === 'true' : false);
  const [clothFlow, setClothFlow] = useState(() => devClothFlow ? devClothFlow === 'true' : false);
  const [clothFade, setClothFade] = useState(() => devClothFade ? devClothFade === 'true' : false);
  
  // Cloth Fade remains deprecated for Modern; Cloth Flow is allowed (Phase 6A)
  useEffect(() => {
    if (patternType === 'modern' && clothFade) {
      console.log('Auto-disabling cloth fade for modern pattern');
      setClothFade(false);
    }
  }, [patternType, clothFade]);
  
  // Color limit feature
  const [colorLimitMode, setColorLimitMode] = useState<'off' | 'auto' | 'manual'>(() => {
    if (devColorLimitMode && ['off', 'auto', 'manual'].includes(devColorLimitMode)) {
      return devColorLimitMode;
    }
    return 'off';
  });
  const [colorLimit, setColorLimit] = useState(() =>
    normalizeColorLimit(devColorLimit ? parseInt(devColorLimit, 10) : COLOR_LIMIT_MIN)
  );
  
  // Remove all other advanced modes
  // const [tileableMode, setTileableMode] = useState(false);
  // const [textureMode, setTextureMode] = useState(false);
  // const [advancedBlending, setAdvancedBlending] = useState(false);
  // const [lightingMode, setLightingMode] = useState(false);
  // const [fabricSimulation, setFabricSimulation] = useState(false);
  // const [performanceMode, setPerformanceMode] = useState(false);

  // Render Queue System
  interface RenderJob {
    id: string;
    config: any;
    timestamp: number;
    status: 'pending' | 'running' | 'completed' | 'cancelled';
    startTime?: number;
    endTime?: number;
  }

  const [renderQueue, setRenderQueue] = useState<RenderJob[]>([]);
  const [currentJob, setCurrentJob] = useState<RenderJob | null>(null);
  const [, setRenderTimes] = useState<number[]>([]);
  const queueProcessorRef = useRef<NodeJS.Timeout | null>(null);

  // Queue system for generated artworks
  // previewPending: true while the one-shot PNG preview is in flight — main/modal must NOT
  // mount a live iframe during this window (that would double-render the same algorithm).
  // When previewPending is false and no displayUrl/previewUrl exists, iframe remains the fallback
  // (PNG failure, favorites without a cached PNG, etc.).
  const [artQueue, setArtQueue] = useState<Array<{
    id: string;
    html: string;
    config: any;
    timestamp: number;
    previewUrl?: string;
    displayUrl?: string;
    previewPending?: boolean;
  }>>([]);
  const artQueueRef = useRef(artQueue);
  artQueueRef.current = artQueue;
  /**
   * Phase 4F / 6L auto-follow:
   * - followLatestReadyIdRef non-null ⇒ follow context is active (cleared on manual select).
   * - followEpochIdsRef = artwork ids prepended while this follow context is active.
   * Main preview converges to the newest READY id in the epoch, not only the exact follow id.
   */
  const followLatestReadyIdRef = useRef<string | null>(null);
  const followEpochIdsRef = useRef<Set<string>>(new Set());

  const clearPreviewFollow = () => {
    followLatestReadyIdRef.current = null;
    followEpochIdsRef.current = new Set();
  };

  const activatePreviewFollowForArt = (artId: string) => {
    if (!followLatestReadyIdRef.current) {
      followEpochIdsRef.current = new Set([artId]);
    } else {
      followEpochIdsRef.current.add(artId);
    }
    followLatestReadyIdRef.current = artId;
  };

  /** Phase 6L: converge main selection to newest ready artwork in the follow epoch. */
  const reconcileFollowToNewestReady = () => {
    if (!shouldReconcileFollowOnPreviewReady(Boolean(followLatestReadyIdRef.current))) {
      return;
    }
    setCurrentArtIndex((prev) => {
      const queue = artQueueRef.current;
      const currentSelectedId =
        prev >= 0 && prev < queue.length ? queue[prev].id : null;
      const nextIdx = resolveFollowSelectionIndex(
        queue,
        followEpochIdsRef.current,
        currentSelectedId
      );
      return nextIdx == null ? prev : nextIdx;
    });
  };

  // Phase 4B: bridge preview scheduler → React state (by stable artwork id).
  // Phase 4D: also require generation eligibility (cancelled jobs never attach previews).
  setPreviewSchedulerBridge({
    isArtPresent: (artId: string) =>
      isGenerationEligible(artId) &&
      artQueueRef.current.some((item) => item.id === artId),
    getCachedPreviewUrl: (previewKey: string) => getSessionPreviewCacheEntry(previewCache, previewKey),
    // Phase 4E: no IndexedDB / durable preview lookup (legacy stored dead blob: strings).
    saveCachedPreviewUrl: (previewKey: string, url: string) => {
      setSessionPreviewCacheEntry(previewCache, previewKey, url);
    },
    renderPreview: (artId, script, width, height) =>
      enqueueWorkerRender(artId, script, width, height, true),
    onPreviewCacheHit: (artId, url) => {
      if (!isGenerationEligible(artId) || !artQueueRef.current.some((item) => item.id === artId)) {
        if (typeof url === 'string' && url.startsWith('blob:')) {
          // Do not retain for a cancelled/deleted art — abandon if we already own it.
          abandonPreviewBlobUrl(url, artId);
        }
        return;
      }
      if (typeof url === 'string' && url.startsWith('blob:')) {
        retainPreviewBlobUrl(url, artId);
      }
      // Eager ref sync so reconcile sees the attach even before React commits;
      // functional setArtQueue re-applies on latest state so bulk prepends are not lost.
      artQueueRef.current = attachPreviewToArtQueue(artQueueRef.current, artId, url);
      setArtQueue((prev) => {
        if (!isGenerationEligible(artId) || !prev.some((item) => item.id === artId)) {
          return prev;
        }
        const next = attachPreviewToArtQueue(prev, artId, url);
        artQueueRef.current = next;
        return next;
      });
      reconcileFollowToNewestReady();
    },
    onPreviewReady: (artId, url) => {
      if (!isGenerationEligible(artId) || !artQueueRef.current.some((item) => item.id === artId)) {
        abandonPreviewBlobUrl(url, artId);
        return;
      }
      artQueueRef.current = attachPreviewToArtQueue(artQueueRef.current, artId, url);
      setArtQueue((prev) => {
        if (!isGenerationEligible(artId) || !prev.some((item) => item.id === artId)) {
          abandonPreviewBlobUrl(url, artId);
          return prev;
        }
        const next = attachPreviewToArtQueue(prev, artId, url);
        artQueueRef.current = next;
        return next;
      });
      reconcileFollowToNewestReady();
      console.log('PREVIEW ATTACH', artId);
      console.log('PNG generated and displayed for art:', artId);
    },
    onPreviewFailed: (artId) => {
      if (!isGenerationEligible(artId) || !artQueueRef.current.some((item) => item.id === artId)) {
        return;
      }
      console.warn('PNG generation failed for art:', artId, '- will use iframe fallback');
      artQueueRef.current = markPreviewFailedInArtQueue(artQueueRef.current, artId);
      setArtQueue((prev) => {
        if (!isGenerationEligible(artId) || !prev.some((item) => item.id === artId)) {
          return prev;
        }
        const next = markPreviewFailedInArtQueue(prev, artId);
        artQueueRef.current = next;
        return next;
      });
      // Failed preview still usable via iframe fallback — reconcile follow if active.
      reconcileFollowToNewestReady();
    },
    abandonPreviewUrl: abandonPreviewBlobUrl,
  });

  const [currentArtIndex, setCurrentArtIndex] = useState(-1);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const stopBulkRef = useRef(false);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  
  // Backend URL toggle state
  const getDefaultBackendUrl = () => {
    // In production, use same-origin proxy rewrite to avoid CORS
    if (window.location.hostname !== 'localhost') {
      return '/backend';
    }
    // In local dev, use CRA proxy path to Vercel backend to avoid CORS
    return '/vercel-backend';
  };
  const [backendUrl, setBackendUrl] = useState(getDefaultBackendUrl());
  const backendOptions = [
    { label: 'Vercel (proxy via /backend)', url: '/backend' },
    { label: 'Vercel (direct)', url: 'https://checks-of-time-beta.vercel.app' },
    { label: 'Render', url: 'https://checks-of-time-beta.onrender.com' },
    { label: 'Local', url: 'http://localhost:3000' },
    { label: 'Local (proxy to Vercel via /vercel-backend)', url: '/vercel-backend' },
  ];
  useEffect(() => {
    try {
      const saved = localStorage.getItem('backendUrl');
      const isLocalhost = window.location.hostname === 'localhost';
      if (isLocalhost) {
        if (saved && saved !== backendUrl) {
          setBackendUrl(saved);
        }
      } else {
        // On production, always force proxy path to avoid CORS and clear stale saved values
        if (backendUrl !== '/backend') setBackendUrl('/backend');
        if (saved && saved !== '/backend') localStorage.removeItem('backendUrl');
      }
    } catch (_) {
      // ignore storage errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('backendUrl', backendUrl);
    } catch (_) {
      // ignore storage errors
    }
  }, [backendUrl]);

  // Helper to post to backend, trying both /generate and /api/generate for compatibility
  const postToGenerate = async (
    baseUrl: string,
    payload: any,
    signal?: AbortSignal
  ): Promise<Response> => {
    // Prefer known working endpoints first to avoid noisy 404/405 in console
    const endpoints = [
      `${baseUrl}/generate`,
      `${baseUrl}/api/generate`,
    ];
    let lastResponse: Response | null = null;
    let lastError: unknown = null;
    for (const endpoint of endpoints) {
      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal,
        });
        if (res.ok) return res;
        lastResponse = res;
      } catch (e) {
        if (isAbortError(e) || signal?.aborted) {
          throw e;
        }
        lastError = e;
        // try next endpoint
      }
    }
    if (lastResponse) return lastResponse;
    if (lastError) throw lastError;
    throw new Error('Failed to reach generation endpoint');
  };

  // Bitcoin integration removed in cleanup


  // Add a flag to track if we loaded from URL
  const [loadedFromURL, setLoadedFromURL] = useState(false);

  const [pendingGeneration, setPendingGeneration] = useState(false);
  // Phase 6J: one-generation Random Madras carry, set only by "Create with these
  // traits" and consumed by the next pending generation. Never persisted.
  const carriedPaletteColorsRef = useRef<string[] | null>(null);

  const [modalArt, setModalArt] = useState<{ id: string; html: string; config: any; timestamp: number; previewUrl?: string; displayUrl?: string; previewPending?: boolean } | null>(null);
  const [modalIndex, setModalIndex] = useState<number>(0); // Add state for modal navigation
  
  // Export functionality state
  const [exportCanvasSize, setExportCanvasSize] = useState(2048);
  const [exportFormat, setExportFormat] = useState('png');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExperimentalNotice, setShowExperimentalNotice] = useState(true);
  const [permalinkError, setPermalinkError] = useState<string | null>(null);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [hasGeneratedFirstArt, setHasGeneratedFirstArt] = useState(false);
  const generationInProgress = useRef(false);

  // Clear frozen colors when leaving a seeded random mode. Do not mint on select.
  useEffect(() => {
    if (!isSeededPaletteMode(palette) && paletteColors) {
      setPaletteColors(null);
    }
    // eslint-disable-next-line
  }, [palette]);

  // Load configuration from URL on component mount
  useEffect(() => {
    if (!hasGeneratedFirstArt && artQueue.length === 0 && !currentJob) {
      loadConfigFromURL();
      // Don't automatically generate art on load - let user click generate button
      setHasGeneratedFirstArt(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Phase 6B: Cloth Flow toggle only updates config state. It must NOT set
  // pendingGeneration (that previously auto-generated on toggle and on mount).

  // Reset the flag if the queue is cleared
  useEffect(() => {
    if (artQueue.length === 0 && hasGeneratedFirstArt) {
      // Only reset if we're not in the middle of initial load
      setTimeout(() => {
        setHasGeneratedFirstArt(false);
      }, 100);
    }
  }, [artQueue.length, hasGeneratedFirstArt]);

  // Bitcoin integration removed

  // DEV: Update URL for development testing
  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      // Do not clobber canonical /g/{id} permalinks
      if (parseGenerationPath(window.location.pathname)) {
        return;
      }
      const params = new URLSearchParams();
      if (seed) params.set('seed', seed);
      if (patternType) params.set('pattern', patternType);
      // Do not sync palette into the query string — that would lock the
      // one-shot random initial palette across refreshes. Explicit ?palette=
      // overrides are still honored on first load via the useState initializer.
      if (threadSpacing) params.set('threadSpacing', threadSpacing.toString());
      if (bgColor) params.set('bgColor', bgColor);
      if (lint !== undefined) params.set('lint', lint.toString());
      if (diagonalMode !== undefined) params.set('diagonalMode', diagonalMode.toString());
      if (clothFlow !== undefined) params.set('clothFlow', clothFlow.toString());
      if (clothFade !== undefined) params.set('clothFade', clothFade.toString());
      if (densityPreset) params.set('densityPreset', densityPreset);
      if (colorLimitMode) params.set('colorLimitMode', colorLimitMode);
      if (colorLimit) params.set('colorLimit', colorLimit.toString());
      
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      if (newUrl !== window.location.pathname + window.location.search) {
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [seed, patternType, threadSpacing, bgColor, lint, diagonalMode, clothFlow, clothFade, densityPreset, colorLimitMode, colorLimit]);

  // Update modal when new art is generated for seed links, and when preview PNG lands for an open modal
  useEffect(() => {
    if (!modalArt) return;

    if (modalArt.html === '' && artQueue.length > 0) {
      // If modal is open with empty HTML and new art is generated, update the modal
      const latestArt = artQueue[0];
      setModalArt(latestArt);
      setModalIndex(0);
      return;
    }

    const updated = artQueue.find(a => a.id === modalArt.id);
    if (
      updated &&
      (updated.displayUrl !== modalArt.displayUrl ||
        updated.previewUrl !== modalArt.previewUrl ||
        updated.previewPending !== modalArt.previewPending ||
        (updated.html && updated.html !== modalArt.html))
    ) {
      setModalArt(updated);
    }
  }, [artQueue, modalArt]);

  // Don't automatically update URL - let user control when to share
  useEffect(() => {
    if (loadedFromURL) {
      // Skip updates when loading from URL
      setLoadedFromURL(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedFromURL]);

  // useEffect to trigger art generation after all config state is updated
  useEffect(() => {
    if (pendingGeneration && !generationInProgress.current) {
      console.log('Pending generation triggered');
      // Consume the one-generation palette carry (Create with these traits).
      const carriedPaletteColors = carriedPaletteColorsRef.current;
      carriedPaletteColorsRef.current = null;
      // Use the latest config
      const config = getCurrentConfig(carriedPaletteColors);
      generateNewArt(config);
      setPendingGeneration(false);
    }
    // eslint-disable-next-line
  }, [pendingGeneration]);

  // Function to ensure UI state consistency
  const ensureUIStateConsistency = () => {
    // Ensure clothFade is always a boolean
    if (typeof clothFade !== 'boolean') {
      console.warn('Fixing clothFade state inconsistency:', clothFade);
      setClothFade(false);
    }
    
    // Ensure clothFlow is always a boolean
    if (typeof clothFlow !== 'boolean') {
      console.warn('Fixing clothFlow state inconsistency:', clothFlow);
      setClothFlow(false);
    }
    
    // Ensure diagonalMode is always a boolean
    if (typeof diagonalMode !== 'boolean') {
      console.warn('Fixing diagonalMode state inconsistency:', diagonalMode);
      setDiagonalMode(false);
    }
  };

  // Function to generate new art and add to queue
  const generateNewArt = async (overrideConfig?: any, skipRandomSeed: boolean = false) => {
    // FIXED: Ensure UI state consistency before generation
    ensureUIStateConsistency();
    
    let config;
    if (overrideConfig) {
      config = { ...overrideConfig };
    } else {
      config = getCurrentConfig();
      // Don't automatically update URL - let user control when to share
    }
    
    // Only generate new random seed if not skipping and user hasn't manually set one
    if (!skipRandomSeed) {
      const isManualSeed = config.seed && (config.seed.length !== 16 || !/^[0-9a-f]{16}$/.test(config.seed));
      if (!isManualSeed) {
        setRandomSeed(true);
      }
    }
    
    // Handle Random Madras palette
    const finalConfig = normalizeConfigForAPI(config);
    
    // DEBUG: Log config for single generation comparison
    if (diagonalMode) {
      console.log(`Single generation config:`, {
        diagonalMode: config.diagonalMode,
        clothFade: config.clothFade,
        clothFlow: config.clothFlow,
        patternType: config.patternType,
        randomAll: randomAll,
        randomSeed: randomSeed,
        randomPaletteToggle: randomPaletteToggle,
        randomBgColor: randomBgColor,
        randomLint: randomLint
      });
      console.log(`Single generation finalConfig sent to backend:`, {
        diagonalMode: finalConfig.diagonalMode,
        clothFade: finalConfig.clothFade,
        clothFlow: finalConfig.clothFlow,
        patternType: finalConfig.patternType
      });
    }
    
    // Add to render queue instead of generating immediately
    enqueueRenderJob(finalConfig);
  };

  // Function to generate multiple artworks in sequence with progressive display
  const generateMultipleArt = async (count: number) => {
    // Phase 4E: do not wipe session preview cache on bulk gen — keys include full config.
    // (Previously clearCachedGenerations wiped dead IDB blob: strings and the Map.)
    
    // FIXED: Ensure UI state consistency before bulk generation
    ensureUIStateConsistency();
    
    setIsBulkGenerating(true);
    stopBulkRef.current = false;
    
    // Add multiple jobs to the queue
    for (let i = 0; i < count; i++) {
      if (stopBulkRef.current) {
        break;
      }
      // Always get a fresh config for every generation using unified approach
      const config = getUnifiedConfig(generateRandomSeed());
      const finalConfig = normalizeConfigForAPI(config);
      
      // DEBUG: Log config to verify clothFade setting
      if (diagonalMode) {
        console.log(`Bulk generation ${i + 1} config:`, {
          diagonalMode: config.diagonalMode,
          clothFade: config.clothFade,
          clothFlow: config.clothFlow,
          patternType: config.patternType,
          randomAll: randomAll,
          randomSeed: randomSeed,
          randomPaletteToggle: randomPaletteToggle,
          randomBgColor: randomBgColor,
          randomLint: randomLint
        });
        console.log(`Bulk generation ${i + 1} finalConfig sent to backend:`, {
          diagonalMode: finalConfig.diagonalMode,
          clothFade: finalConfig.clothFade,
          clothFlow: finalConfig.clothFlow,
          patternType: finalConfig.patternType
        });
      }
      
      // Add to render queue
      enqueueRenderJob(finalConfig);
    }
    
    setIsBulkGenerating(false);
    stopBulkRef.current = false;
  };

  // Function to stop bulk generation
  const stopBulkGeneration = () => {
    stopBulkRef.current = true;
    setIsBulkGenerating(false);
    // Also clear the render queue when stopping bulk generation
    clearRenderQueue();
  };

  // Render Queue Processing Functions
  const processQueue = async () => {
    console.log('processQueue called:', { currentJob: !!currentJob, renderQueueLength: renderQueue.length });
    if (currentJob || renderQueue.length === 0) {
      console.log('processQueue early return:', { currentJob: !!currentJob, renderQueueLength: renderQueue.length });
      return;
    }

    const nextJob = renderQueue[0];
    setCurrentJob({ ...nextJob, status: 'running', startTime: Date.now() });
    setRenderQueue(prev => prev.slice(1));

    const { signal } = beginGenerationAbort(nextJob.id);

    try {
      console.log('Processing job:', nextJob.id, 'with config:', nextJob.config);
      const finalConfig = normalizeConfigForAPI(nextJob.config);
      console.log('Normalized config:', finalConfig);
      console.log('Making request to backend:', backendUrl);
      const res = await postToGenerate(backendUrl, finalConfig, signal);

      // Late cancel after response headers but before append
      if (!isGenerationEligible(nextJob.id) || signal.aborted) {
        console.log('Generation cancelled; discarding API result for job:', nextJob.id);
        setCurrentJob({ ...nextJob, status: 'cancelled', endTime: Date.now() });
        return;
      }
      
      console.log('Backend response status:', res.status, res.ok);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const html = await res.text();

      if (!isGenerationEligible(nextJob.id) || signal.aborted) {
        console.log('Generation cancelled during body read; discarding for job:', nextJob.id);
        setCurrentJob({ ...nextJob, status: 'cancelled', endTime: Date.now() });
        return;
      }

      console.log('Received HTML response, length:', html.length);
      if (!isGenerationEligible(nextJob.id) || signal.aborted) {
        console.log('Generation cancelled before append; discarding for job:', nextJob.id);
        setCurrentJob({ ...nextJob, status: 'cancelled', endTime: Date.now() });
        return;
      }

      const newArt = {
        id: nextJob.id,
        html,
        config: { ...finalConfig },
        timestamp: Date.now(),
        previewUrl: undefined as string | undefined,
        displayUrl: undefined as string | undefined,
        // Hold main/modal on a placeholder until the single PNG render finishes
        previewPending: true
      };

      if (!isGenerationEligible(nextJob.id)) {
        console.log('Generation cancelled during append; artwork not kept:', nextJob.id);
        setCurrentJob({ ...nextJob, status: 'cancelled', endTime: Date.now() });
        return;
      }

      // CRITICAL: Sync artQueueRef BEFORE enqueueing preview.
      // previewScheduler.runJob checks isArtPresent (via artQueueRef) synchronously on
      // enqueue/pump. React's setArtQueue has not committed yet, so without this the
      // job is dropped immediately and previewPending stays true forever.
      const previousLength = artQueueRef.current.filter((item) => item.id !== newArt.id).length;
      artQueueRef.current = [
        newArt,
        ...artQueueRef.current.filter((item) => item.id !== newArt.id),
      ];
      setArtQueue((prev) => {
        if (!isGenerationEligible(nextJob.id)) {
          return prev;
        }
        if (prev.some((item) => item.id === newArt.id)) {
          return prev;
        }
        return [newArt, ...prev];
      });
      // Phase 4F/6L: keep showing the previous READY artwork; follow epoch advances
      // to newest READY when any preview in the epoch finishes.
      activatePreviewFollowForArt(newArt.id);
      setCurrentArtIndex((prev) => shiftIndexAfterPrepend(prev, previousLength));
      console.log('Successfully added art to queue:', newArt.id);

      // Phase 4B: enqueue preview on the independent scheduler (do NOT await).
      console.log('PREVIEW ENQUEUE REQUEST', newArt.id);
      enqueueArtworkPreview({
        artId: newArt.id,
        html,
        config: { ...finalConfig },
        previewKey: computePreviewKey(finalConfig),
      });
      // Update render time tracking (API generation duration only)
      const endTime = Date.now();
      const renderTime = endTime - (nextJob.startTime || endTime);
      setRenderTimes(prev => {
        const newTimes = [...prev, renderTime].slice(-10); // Keep last 10 times
        const avg = newTimes.reduce((sum, time) => sum + time, 0) / newTimes.length;
        console.log(`Average render time: ${avg.toFixed(0)}ms (last ${newTimes.length} jobs)`);
        return newTimes;
      });

      if (isGenerationEligible(nextJob.id)) {
        setCurrentJob({ ...nextJob, status: 'completed', endTime });
      } else {
        setCurrentJob({ ...nextJob, status: 'cancelled', endTime });
      }
      
    } catch (e) {
      if (isAbortError(e) || !isGenerationEligible(nextJob.id)) {
        console.log('Generation aborted for job:', nextJob.id);
        setCurrentJob({ ...nextJob, status: 'cancelled', endTime: Date.now() });
      } else {
        console.error('Failed to process render job:', e);
        setCurrentJob({ ...nextJob, status: 'cancelled', endTime: Date.now() });
      }
    } finally {
      releaseGenerationAbort(nextJob.id);
    }

    // Process next job in queue
    queueProcessorRef.current = setTimeout(() => {
      console.log('Setting currentJob to null to process next job');
      setCurrentJob(null);
    }, 100);
  };

  const enqueueRenderJob = (config: any) => {
    const job: RenderJob = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      config,
      timestamp: Date.now(),
      status: 'pending'
    };
    
    console.log('Enqueueing job:', job.id);
    setRenderQueue(prev => [...prev, job]);
  };

  /**
   * Cancel the in-flight API generation job.
   * - Aborts fetch via per-job AbortController
   * - Cancels preview scheduler entry if artwork already exists
   * - Removes incomplete (previewPending) artwork created by this job
   * - Does not clear completed artworks or unrelated pending queue jobs
   */
  const cancelCurrentJob = () => {
    if (!currentJob) return;
    const jobId = currentJob.id;
    // End bulk auto-follow when the followed job is cancelled so late preview
    // callbacks cannot revive stale follow behavior (Phase 6L Test F).
    followEpochIdsRef.current.delete(jobId);
    if (followLatestReadyIdRef.current === jobId) {
      clearPreviewFollow();
    }
    abortGenerationJob(jobId);
    artworkPreviewScheduler.cancelArt(jobId);

    // If artwork was already appended for this job, remove it and release blobs.
    setArtQueue((prev) => {
      const removed = prev.filter((item) => item.id === jobId);
      if (removed.length) {
        cleanupArtsPreviewResources(removed);
      }
      const updated = prev.filter((item) => item.id !== jobId);
      artQueueRef.current = updated;
      const removedFront = prev[0]?.id === jobId;
      const preferred = removedFront ? Math.max(0, currentArtIndex - 1) : currentArtIndex;
      setCurrentArtIndex(resolveIndexAfterDeletion(updated, preferred));
      return updated;
    });
    setModalArt((prev) => (prev && prev.id === jobId ? null : prev));

    setCurrentJob({ ...currentJob, status: 'cancelled', endTime: Date.now() });
    queueProcessorRef.current = setTimeout(() => {
      setCurrentJob(null);
    }, 100);
  };

  /** Clear pending generation jobs only — does not delete completed artworks. */
  const clearRenderQueue = () => {
    setRenderQueue((prev) => {
      // Pending jobs never started fetch; mark cancelled so they cannot be revived if raced.
      markGenerationsCancelled(prev.map((j) => j.id));
      return [];
    });
  };


  

  


  // Auto-process queue when it changes
  useEffect(() => {
    const hasCurrentJob = !!currentJob;
    console.log('Queue effect triggered:', { renderQueueLength: renderQueue.length, currentJob: hasCurrentJob });
    if (renderQueue.length > 0 && !hasCurrentJob) {
      console.log('Starting to process queue');
      processQueue();
    }
    // processQueue omitted on purpose: wrapping it in useCallback caused stale closures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderQueue.length, currentJob]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (queueProcessorRef.current) {
        clearTimeout(queueProcessorRef.current);
      }
    };
  }, []);

  // True fullscreen API logic
  useEffect(() => {
    function handleFullscreenChange() {
      const isNowFullscreen = document.fullscreenElement === canvasContainerRef.current;
      setIsFullscreen(isNowFullscreen);
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!isFullscreen && canvasContainerRef.current) {
      canvasContainerRef.current.requestFullscreen();
    } else if (isFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  // Ensure currentArtIndex is always valid for the current artQueue
  useEffect(() => {
    if (artQueue.length > 0 && currentArtIndex >= artQueue.length) {
      setCurrentArtIndex(resolveIndexAfterDeletion(artQueue, artQueue.length - 1));
    } else if (artQueue.length === 0 && currentArtIndex !== -1 && currentArtIndex !== 0) {
      setCurrentArtIndex(-1);
    }
  }, [artQueue, currentArtIndex]);

  // Phase 6L: when returning from a background tab during active bulk follow,
  // reconcile the main preview to the newest ready artwork in the follow epoch.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!followLatestReadyIdRef.current) return;
      reconcileFollowToNewestReady();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // FIXED: Monitor and fix state inconsistencies
  useEffect(() => {
    // Check for state inconsistencies and fix them
    if (typeof clothFade !== 'boolean') {
      console.warn('Detected clothFade state inconsistency, fixing...');
      setClothFade(false);
    }
    if (typeof clothFlow !== 'boolean') {
      console.warn('Detected clothFlow state inconsistency, fixing...');
      setClothFlow(false);
    }
    if (typeof diagonalMode !== 'boolean') {
      console.warn('Detected diagonalMode state inconsistency, fixing...');
      setDiagonalMode(false);
    }
  }, [clothFade, clothFlow, diagonalMode]);

  const previewProgress = useMemo(() => countPreviewProgress(artQueue), [artQueue]);
  const previewStatusText = useMemo(
    () => formatPreviewRenderingStatus(previewProgress),
    [previewProgress]
  );

  // Phase 4F: paint READY artwork only — never blank pending when a ready art exists.
  const currentArt = useMemo(() => {
    return pickDisplayedArt(artQueue, currentArtIndex);
  }, [artQueue, currentArtIndex]);

  const displayedArtIndex = useMemo(() => {
    if (!currentArt) return currentArtIndex;
    const idx = indexOfArtId(artQueue, currentArt.id);
    return idx >= 0 ? idx : currentArtIndex;
  }, [artQueue, currentArt, currentArtIndex]);

  // Navigation functions
  const goToPrevious = () => {
    clearPreviewFollow();
    for (let i = displayedArtIndex - 1; i >= 0; i--) {
      if (isPreviewImageReady(artQueue[i]) || artQueue[i].previewPending === false) {
        setCurrentArtIndex(i);
        if (artQueue[i]) {
          updateURLWithConfig(artQueue[i].config);
        }
        return;
      }
    }
  };

  const goToNext = () => {
    clearPreviewFollow();
    for (let i = displayedArtIndex + 1; i < artQueue.length; i++) {
      if (isPreviewImageReady(artQueue[i]) || artQueue[i].previewPending === false) {
        setCurrentArtIndex(i);
        if (artQueue[i]) {
          updateURLWithConfig(artQueue[i].config);
        }
        return;
      }
    }
  };

  const goToLatest = () => {
    if (artQueue.length === 0) return;
    if (isPreviewImageReady(artQueue[0])) {
      clearPreviewFollow();
      setCurrentArtIndex(0);
      return;
    }
    // Newest still pending — keep current READY visible; auto-switch when it finishes.
    activatePreviewFollowForArt(artQueue[0].id);
  };



  // Random generation functions
  const generateRandomSeed = () => {
    return Array.from({length: 16}, () => Math.floor(Math.random()*16).toString(16)).join('');
  };

  // Removed unused generateRandomThreadSpacing

  const generateRandomBgColor = () => {
    const colors = ['#222', '#333', '#444', '#555', '#666', '#777', '#888', '#999', '#aaa', '#bbb'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const generateRandomLint = () => {
    return Math.random() > 0.5;
  };



  // Bitcoin features removed

  // Permalink: versioned binary → base62 at /g/{id}
  const updateURLWithConfig = useCallback((config: any) => {
    try {
      const id = encodeConfigToPermalinkId(config);
      const path = buildLocalPermalinkPath(id);
      window.history.replaceState({}, '', path);
      setPermalinkError(null);
    } catch (e) {
      console.warn('Failed to update permalink:', e);
    }
  }, []);

  // Modal navigation functions
  const goToPreviousModal = useCallback(() => {
    if (modalIndex > 0) {
      const newIndex = modalIndex - 1;
      setModalIndex(newIndex);
      setModalArt(artQueue[newIndex]);
      updateURLWithConfig(artQueue[newIndex].config);
    }
  }, [modalIndex, artQueue, updateURLWithConfig]);

  const goToNextModal = useCallback(() => {
    if (modalIndex < artQueue.length - 1) {
      const newIndex = modalIndex + 1;
      setModalIndex(newIndex);
      setModalArt(artQueue[newIndex]);
      updateURLWithConfig(artQueue[newIndex].config);
    }
  }, [modalIndex, artQueue, updateURLWithConfig]);

  // Function to close modal and clear generation permalink path
  const closeModal = () => {
    setModalArt(null);
    if (parseGenerationPath(window.location.pathname)) {
      window.history.replaceState({}, '', '/');
    }
  };

  // Function to handle seed links by opening gallery preview modal
  const handleSeedLink = (config: any) => {
    const tempArt = {
      id: `seed_${Date.now()}`,
      html: '',
      config: config,
      timestamp: Date.now()
    };
    setModalArt(tempArt);
    generateNewArt(config);
  };

  const loadConfigFromURL = () => {
    const id = parseGenerationPath(window.location.pathname);
    if (!id) {
      return false;
    }

    const decoded = decodeGenerationId(id);
    if (decoded.ok === false) {
      setPermalinkError(decoded.error);
      return false;
    }

    const loadedConfig = decoded.state;
    setPermalinkError(null);
    setLoadedFromURL(true);
    setSeed(loadedConfig.seed);
    setPalette(
      loadedConfig.paletteName ||
        (typeof loadedConfig.palette === 'string' ? loadedConfig.palette : 'Random Madras')
    );
    setPaletteColors(loadedConfig.paletteColors || null);
    setThreadSpacing(loadedConfig.threadSpacing);
    setBgColor(loadedConfig.bgColor);
    setLint(loadedConfig.lint);
    setOpaque(loadedConfig.opaque);
    setDiagonalMode(loadedConfig.diagonalMode || false);
    setClothFlow(Boolean(loadedConfig.clothFlow));
    setClothFade(Boolean(loadedConfig.clothFade));
    setPatternType((loadedConfig.patternType as any) || 'classic');
    setColorLimitMode((loadedConfig.colorLimitMode as any) || 'off');
    setColorLimit(normalizeColorLimit(loadedConfig.colorLimit, 4));
    const fullConfig = {
      ...loadedConfig,
      threadVariance: loadedConfig.threadVariance || 0.3,
    };
    handleSeedLink(fullConfig);
    return true;
  };

  // Function to get current config with random values applied.
  // carriedPaletteColors: explicit one-generation seeded-palette carry
  // ("Create with these traits"); normal generations pass nothing.
  const getCurrentConfig = (carriedPaletteColors: string[] | null = null) => {
    let currentSeed = seed;
    let currentThreadSpacing = threadSpacing;
    let currentBgColor = bgColor;
    let currentLint = lint;
    
    // Always apply random values when random toggles are enabled
    if (randomAll || randomSeed) {
      currentSeed = generateRandomSeed();
      setSeed(currentSeed); // Update state with new seed
    }

    const namedPalettes = listNamedPalettes(PALETTE_COLORS);
    const paletteResolved = resolveGenerationPalette({
      palette,
      randomPaletteToggle,
      randomAll,
      namedPalettes,
      createRandomMadrasPalette,
      createRandomHarmonyPalette,
      colorLimitMode,
      colorLimit,
      // Seeded modes mint from the seed; carried colors apply to this
      // generation only when "Create with these traits" provided them.
      existingPaletteColors: carriedPaletteColors,
      preserveResolvedPalette: Boolean(carriedPaletteColors),
      seed: currentSeed,
    });
    const currentPalette = paletteResolved.palette;
    const currentPaletteColors = paletteResolved.paletteColors;
    if (paletteResolved.uiPaletteUpdate) {
      setPalette(paletteResolved.uiPaletteUpdate);
      setPaletteColors(null);
    }

    // weave tightness randomization replaced by density presets; no longer used
    if (randomAll || randomBgColor) {
      currentBgColor = generateRandomBgColor();
      setBgColor(currentBgColor); // Update state with new background color
    }
    if (randomAll || randomLint) {
      currentLint = generateRandomLint();
      setLint(currentLint); // Update state with new lint setting
    }
    
    // Bitcoin seed modes removed

          // Map density preset to classic/bleed/modern parameters
    function pickDensityWithin(low: number, high: number) {
      return low + Math.random() * Math.max(0, high - low);
    }
    let derivedThreadSpacing = currentThreadSpacing;
    if (densityPreset === 'random') {
      derivedThreadSpacing = pickDensityWithin(3, 6);
    } else if (densityPreset === 'airy') {
      derivedThreadSpacing = pickDensityWithin(5, 6);
    } else if (densityPreset === 'balanced') {
      derivedThreadSpacing = pickDensityWithin(4, 5);
    } else if (densityPreset === 'tight') {
      derivedThreadSpacing = pickDensityWithin(2, 3);
    }

    // For plaid and woolen we'll send threadWidth/threadGap overrides derived from the same preset
    let bleedThreadWidth: number | undefined;
    let modernThreadWidth: number | undefined;
    let modernThreadGap: number | undefined;
    if (densityPreset === 'random') {
      bleedThreadWidth = pickDensityWithin(2, 7);
      modernThreadWidth = pickDensityWithin(1, 3);
      modernThreadGap = pickDensityWithin(1, 3);
    } else if (densityPreset === 'airy') {
      bleedThreadWidth = pickDensityWithin(5, 7);
      modernThreadWidth = pickDensityWithin(2.2, 3);
      modernThreadGap = pickDensityWithin(2, 3);
    } else if (densityPreset === 'balanced') {
      bleedThreadWidth = pickDensityWithin(3, 5);
      modernThreadWidth = pickDensityWithin(1.6, 2.4);
      modernThreadGap = pickDensityWithin(1.4, 2);
    } else if (densityPreset === 'tight') {
      bleedThreadWidth = pickDensityWithin(2, 3.5);
      modernThreadWidth = pickDensityWithin(1.2, 1.8);
      modernThreadGap = pickDensityWithin(1, 1.6);
    }

    // Cloth Fade UI is hidden; keep generator path but do not apply shading
    const explicitClothFade = false;
    
    const baseConfig = {
      seed: currentSeed,
      palette: currentPalette,
      paletteColors: isSeededPaletteMode(currentPalette) ? currentPaletteColors : null,
      threadSpacing: derivedThreadSpacing,
      bgColor: currentBgColor,
      lint: currentLint,
      opaque,
      threadVariance: 0.3,
                        // NEW: Advanced pattern modes
                        diagonalMode,
      clothFlow,
      clothFade: explicitClothFade,
      patternType,
      // New density-driven overrides for styles
      ...(patternType === 'bleed' ? { threadWidth: bleedThreadWidth } : {}),
      ...(patternType === 'modern' ? { threadWidth: modernThreadWidth, threadGap: modernThreadGap } : {}),
      // Color limit feature
      colorLimitMode,
      colorLimit
    };

    return baseConfig;
  };

  // Unified config function for both single and bulk generation
  const getUnifiedConfig = (overrideSeed?: string) => {
    // Use getCurrentConfig as the single source of truth
    const baseConfig = getCurrentConfig();
    
    // For bulk generation, override the seed if provided (for variety)
    if (overrideSeed) {
      baseConfig.seed = overrideSeed;
      // Seeded palette modes are generators: bulk items each mint from their own seed.
      if (baseConfig.palette === RANDOM_MADRAS) {
        baseConfig.paletteColors = createRandomMadrasPalette(overrideSeed);
      } else if (baseConfig.palette === RANDOM_PALETTE) {
        baseConfig.paletteColors = createRandomHarmonyPalette({
          seed: overrideSeed,
          colorLimitMode,
          colorLimit,
        });
      }
    }
    
    return baseConfig;
  };

  // Only regenerate random palette on Generate Art
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const regenerate = () => {
    const newSeed = Array.from({length: 16}, () => Math.floor(Math.random()*16).toString(16)).join('');
    setSeed(newSeed);
    // Random Madras colors are minted at generation time, not here.
    setPendingGeneration(true);
  };

  // Removed unused saveToGallery (handled via gallery modals)

  // Gallery management functions
  const toggleGalleryItemSelection = (itemId: string) => {
    setSelectedGalleryItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const selectAllGalleryItems = () => {
    const currentItems = getCurrentGalleryItems();
    setSelectedGalleryItems(new Set(currentItems.map(item => item.id)));
  };

  const deselectAllGalleryItems = () => {
    setSelectedGalleryItems(new Set());
  };



  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleListed = (itemId: string) => {
    setListedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const deleteGalleryItems = (itemIds: string[]) => {
    // Cancel any pending/in-flight preview jobs for these artworks (Phase 4B/4D).
    markGenerationsCancelled(itemIds);
    artworkPreviewScheduler.cancelArts(itemIds);
    if (followLatestReadyIdRef.current && itemIds.includes(followLatestReadyIdRef.current)) {
      clearPreviewFollow();
    } else {
      for (const id of itemIds) {
        followEpochIdsRef.current.delete(id);
      }
    }

    // Handle queue items deletion
    if (galleryView === 'queue') {
      setArtQueue(prev => {
        const removed = prev.filter(item => itemIds.includes(item.id));
        cleanupArtsPreviewResources(removed);

        const updated = prev.filter(item => !itemIds.includes(item.id));
        artQueueRef.current = updated;

        let newIndex = currentArtIndex;
        if (currentArtIndex >= updated.length) {
          newIndex = Math.max(0, updated.length - 1);
        } else {
          const currentArtId = prev[currentArtIndex]?.id;
          if (currentArtId && itemIds.includes(currentArtId)) {
            newIndex = Math.max(0, Math.min(currentArtIndex, updated.length - 1));
          }
        }
        setTimeout(() => setCurrentArtIndex(resolveIndexAfterDeletion(updated, newIndex)), 0);

        return updated;
      });
      // Drop modal if it referenced a removed queue item (avoid using a revoked blob URL).
      setModalArt(prev => (prev && itemIds.includes(prev.id) ? null : prev));
    } else {
      // Handle gallery items deletion (favorites typically lack preview blob URLs)
      setGallery(prev => {
        const removed = prev.filter(item => itemIds.includes(item.id));
        cleanupArtsPreviewResources(removed);
        const updated = prev.filter(item => !itemIds.includes(item.id));
        const persist = saveFavoritesToLocalStorage(updated);
        if (!persist.ok) {
          console.warn('Failed to persist favorites after delete:', persist.reason);
        }
        return updated;
      });
      setModalArt(prev => (prev && itemIds.includes(prev.id) ? null : prev));
    }
    
    setSelectedGalleryItems(prev => {
      const newSet = new Set(prev);
      itemIds.forEach(id => newSet.delete(id));
      return newSet;
    });
  };

  /** Remove queue artworks and revoke their preview blobs (single-delete control). */
  const removeArtQueueItems = (itemIds: string[]) => {
    markGenerationsCancelled(itemIds);
    artworkPreviewScheduler.cancelArts(itemIds);
    setArtQueue(prev => {
      const removed = prev.filter(item => itemIds.includes(item.id));
      cleanupArtsPreviewResources(removed);
      const updated = prev.filter(item => !itemIds.includes(item.id));
      artQueueRef.current = updated;
      if (followLatestReadyIdRef.current && itemIds.includes(followLatestReadyIdRef.current)) {
        clearPreviewFollow();
      } else {
        for (const id of itemIds) {
          followEpochIdsRef.current.delete(id);
        }
      }
      const preferred =
        currentArtIndex >= updated.length
          ? Math.max(0, updated.length - 1)
          : currentArtIndex;
      setTimeout(() => setCurrentArtIndex(resolveIndexAfterDeletion(updated, preferred)), 0);
      return updated;
    });
    setModalArt(prev => (prev && itemIds.includes(prev.id) ? null : prev));
  };

  const getCurrentGalleryItems = () => {
    switch (galleryView) {
      case 'queue':
        // Phase 4F: hide blank pending cards; artQueue order of ready items preserved.
        return filterReadyPreviewGalleryItems(artQueue);
      case 'listed':
        return gallery.filter(item => listedItems.has(item.id));
      default:
        return gallery; // favorites = all saved gallery items
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getBackgroundLabel = (bgColor: string) => {
    const color = SKIN_COLORS.find(c => c.value === bgColor);
    return color ? color.label : 'Unknown';
  };

  // Export functions — prefer existing Worker/iframe preview raster; iframe art.html only as fallback.
  const exportAsPNG = async (art: any, size: number = 2048) => {
    try {
      const result = await ExportReuse.exportArtAtSize(art, size, {
        mimeType: 'image/png',
        fileExtension: 'png',
      });
      if (result.usedPreviewReuse) {
        console.log('PNG export reused preview raster', {
          source: [result.sourceWidth, result.sourceHeight],
          size,
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const exportAsTIFF = async (art: any, size: number = 2048) => {
    try {
      console.log('Starting TIFF export...', { size, artId: art.id });
      // Existing behavior: high-quality PNG bytes saved with .tiff extension (UTIF unused).
      const result = await ExportReuse.exportArtAtSize(art, size, {
        mimeType: 'image/png',
        quality: 1.0,
        fileExtension: 'tiff',
        onSuccess: () => {
          console.log('TIFF-compatible export completed successfully');
          alert('TIFF export completed! File saved as high-quality PNG with .tiff extension for professional printing.');
        },
      });
      if (result.usedPreviewReuse) {
        console.log('TIFF export reused preview raster', {
          source: [result.sourceWidth, result.sourceHeight],
          size,
        });
      } else {
        console.log('Canvas found (iframe fallback):', {
          width: result.sourceWidth,
          height: result.sourceHeight,
        });
      }
    } catch (error: any) {
      console.error('TIFF export failed:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert(`TIFF export failed: ${error.message}. Please try PNG export instead.`);
    }
  };

  const exportAsJPEG = async (art: any, size: number = 2048) => {
    try {
      await ExportReuse.exportArtAtSize(art, size, {
        mimeType: 'image/jpeg',
        quality: 0.9,
        fileExtension: 'jpg',
      });
    } catch (error) {
      console.error('JPEG export failed:', error);
      alert('JPEG export failed. Please try again.');
    }
  };

  const exportAsWebP = async (art: any, size: number = 2048) => {
    try {
      await ExportReuse.exportArtAtSize(art, size, {
        mimeType: 'image/webp',
        quality: 0.9,
        fileExtension: 'webp',
      });
    } catch (error) {
      console.error('WebP export failed:', error);
      alert('WebP export failed. Please try again.');
    }
  };

  // openExportModal kept and used in UI; do not remove

  const closeExportModal = () => {
    setShowExportModal(false);
  };

  // Move Escape key handler to top-level useEffect
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { 
              if (e.key === 'Escape') closeModal();
      if (modalArt) {
        if (e.key === 'ArrowLeft' && modalIndex > 0) {
          goToPreviousModal();
        }
        if (e.key === 'ArrowRight' && modalIndex < artQueue.length - 1) {
          goToNextModal();
        }
      }
    };
    if (modalArt) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modalArt, modalIndex, artQueue.length, goToNextModal, goToPreviousModal]);

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const generateQueueActive = renderQueue.length > 0 || !!currentJob || !!previewStatusText;

  return (
    <div className={`EOL-dark-root${isFullscreen ? ' is-canvas-fullscreen' : ''}${generateQueueActive ? ' is-generate-queue-active' : ''}`}>
      {permalinkError && (
        <div
          role="alert"
          style={{
            margin: '12px 16px 0',
            padding: '12px 16px',
            background: '#2a1515',
            border: '1px solid #663333',
            borderRadius: 8,
            color: '#f5c2c2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span>Could not open generation link: {permalinkError}</span>
          <button
            type="button"
            onClick={() => {
              setPermalinkError(null);
              window.history.replaceState({}, '', '/');
            }}
            style={{
              background: 'transparent',
              border: '1px solid #885555',
              color: '#f5c2c2',
              borderRadius: 6,
              padding: '6px 10px',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {showAboutModal && (
        <div
          className="EOL-about-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="about-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAboutModal(false);
          }}
        >
          <div className="EOL-about-dialog">
            <div className="EOL-about-header">
              <h2 id="about-title">About</h2>
              <button
                type="button"
                className="EOL-about-close"
                aria-label="Close about"
                onClick={() => setShowAboutModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <p>
              Madras Checks is an independent generative textile project by{' '}
              <a href="https://valipokkann.in/" target="_blank" rel="noopener noreferrer">
                valipokkann
              </a>
              .
            </p>
            <p>
              A computational exploration of Madras textiles, where code meets cloth, pattern, colour and craft.
            </p>
            <p>
              Read more about the project, its history and the work behind it at{' '}
              <a href="https://madraschecks.com/" target="_blank" rel="noopener noreferrer">
                madraschecks.com
              </a>
              .
            </p>
            <p className="EOL-about-copyright">
              &copy; {new Date().getFullYear()} Madras Checks. Created by valipokkann.
            </p>
          </div>
        </div>
      )}

      {showExperimentalNotice && (
        <div
          className="EOL-experimental-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="experimental-notice-title"
        >
          <div className="EOL-experimental-dialog">
            <h2 id="experimental-notice-title">Experimental build</h2>
            <p>
              This site is experimental. Some combinations — especially diagonal + cloth flow + Bleed/Modern at high thread density — are processor intensive and may freeze the tab while rendering. The code is still a work in progress.
            </p>
            <p>
              If your browser asks to Close or Wait, press Wait. The page should return to normal once processing finishes.
            </p>
            <button
              type="button"
              className="EOL-experimental-dismiss"
              onClick={() => setShowExperimentalNotice(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Development Controls - localhost only */}
      {isLocalhost && (
      <div style={{margin: '16px 0', padding: '16px', background: '#1a1a1a', borderRadius: 8, border: '1px solid #333'}}>
        <h3 style={{margin: '0 0 12px 0', fontSize: '1rem', color: '#EB420E', fontWeight: 600}}>
          <Settings size={16} style={{display: 'inline', marginRight: 8}} />
          Development
        </h3>
        
        {/* Backend URL Selector */}
        <div style={{marginBottom: 12}}>
          <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:8}}>
            <label style={{fontSize:'0.9rem', color:'#ccc'}}>Backend</label>
            <select
              value={backendUrl}
              onChange={(e) => setBackendUrl(e.target.value)}
              style={{
                background:'#181818',
                color:'#fff',
                border:'1px solid #333',
                borderRadius:6,
                padding:'6px 8px'
              }}
            >
              {backendOptions.map((opt) => (
                <option key={opt.url} value={opt.url}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div style={{fontSize:'0.85rem',color:'#888'}}>URL: {backendUrl}</div>
          
          {/* Pattern Consistency Test */}
          <div style={{marginTop: 12, padding: '8px', background: '#222', borderRadius: 6, border: '1px solid #333'}}>
            <div style={{fontSize: '0.8rem', color: '#ccc', marginBottom: 8}}>🧪 Pattern Consistency Test</div>
            <div style={{display: 'flex', gap: 4}}>
              <button 
                onClick={() => {
                  const testConfig = {
                    seed: seed || '95e1c9dd11b90ce5',
                    palette: palette,
                    paletteColors: palette === 'Sunset Pondicherry' ? paletteColors : null,
                    threadSpacing: threadSpacing,
                    bgColor: bgColor,
                    lint: lint,
                    opaque,
                    threadVariance: 0.1,
                    diagonalMode,
                    clothFlow,
                    clothFade,
                    patternType: 'classic',
                    colorLimitMode,
                    colorLimit
                  };
                  generateNewArt(testConfig, true);
                }}
                style={{
                  padding: '4px 8px',
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.7rem'
                }}
              >
                Classic
              </button>
              <button 
                onClick={() => {
                  const testConfig = {
                    seed: seed || '95e1c9dd11b90ce5',
                    palette: palette,
                    paletteColors: palette === 'Sunset Pondicherry' ? paletteColors : null,
                    threadSpacing: threadSpacing,
                    bgColor: bgColor,
                    lint: lint,
                    opaque,
                    threadVariance: 0.3,
                    diagonalMode,
                    clothFlow,
                    clothFade,
                    patternType: 'bleed',
                    colorLimitMode,
                    colorLimit
                  };
                  generateNewArt(testConfig, true);
                }}
                style={{
                  padding: '4px 8px',
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.7rem'
                }}
              >
                Bleed
              </button>
              <button 
                onClick={() => {
                  const testConfig = {
                    seed: seed || '95e1c9dd11b90ce5',
                    palette: palette,
                    paletteColors: palette === 'Sunset Pondicherry' ? paletteColors : null,
                    threadSpacing: threadSpacing,
                    bgColor: bgColor,
                    lint: lint,
                    opaque,
                    threadVariance: 0.5,
                    diagonalMode,
                    clothFlow,
                    clothFade,
                    patternType: 'modern',
                    colorLimitMode,
                    colorLimit
                  };
                  generateNewArt(testConfig, true);
                }}
                style={{
                  padding: '4px 8px',
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.7rem'
                }}
              >
                Modern
              </button>
            </div>
          </div>
      </div>

        {/* Seed Input for Development */}
        <div style={{marginBottom: 12}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
            <label style={{fontSize: '0.9rem', color: '#ccc', display: 'flex', alignItems: 'center', gap: 8}}>
              Seed (Dev)
            </label>
            <button 
              onClick={generateRandomSeed}
              style={{
                padding: '4px 8px',
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Dice6 size={14} />
            </button>
          </div>
          <input
            type="text"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            onKeyDown={(e) => {
              // Generate pattern when Enter is pressed
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                // Generate with current seed without changing it - bypass getCurrentConfig.
                // Random Madras mints deterministically from this seed (no carry).
                const manualPalette = resolveGenerationPalette({
                  palette,
                  randomPaletteToggle: false,
                  randomAll: false,
                  namedPalettes: listNamedPalettes(PALETTE_COLORS),
                  createRandomMadrasPalette,
                  createRandomHarmonyPalette,
                  colorLimitMode,
                  colorLimit,
                  seed,
                });
                const manualConfig = {
                  seed: seed,
                  palette: manualPalette.palette,
                  paletteColors: manualPalette.paletteColors,
                  threadSpacing: threadSpacing,
                  bgColor: bgColor,
                  lint: lint,
                  opaque,
                  threadVariance: 0.3,
                  diagonalMode,
                  clothFlow,
                  clothFade,
                  patternType,
                  colorLimitMode,
                  colorLimit
                };
                generateNewArt(manualConfig, true); // Skip random seed generation
              }
            }}
              style={{
              width: '100%',
              padding: '8px 12px',
              background: '#181818',
              border: '1px solid #333',
                borderRadius: 6,
              color: '#fff',
                fontSize: '0.9rem',
              boxSizing: 'border-box'
              }}
            placeholder="Enter seed for development..."
          />
          </div>
          
        {/* Short Permalink UI removed */}
      </div>
      )}


      {/* Top Bar */}
      <header className="EOL-dark-topbar">
        <a
          href="https://madraschecks.com"
          target="_blank"
          rel="noopener noreferrer"
          className="EOL-dark-topbar-left EOL-dark-brand-link"
        >
          <img src={brandLogo} alt="" className="EOL-dark-logo" />
          <span className="EOL-dark-brand-lockup">
            <img
              src={brandWordmark}
              alt="Madras Checks"
              className="EOL-dark-wordmark"
            />
            <span className="EOL-dark-brand-by">by valipokkann</span>
          </span>
        </a>
        <nav className="EOL-dark-nav">
          <button
            type="button"
            className="EOL-dark-nav-button"
            onClick={() => setShowAboutModal(true)}
          >
            About
          </button>
        </nav>
      </header>
      <div className="EOL-dark-main-layout">
        {/* Sidebar Controls */}
        <aside className="EOL-dark-sidebar">
          {/* Main Generation Controls (desktop in-flow; hidden on mobile in favor of dock) */}
          <div className="EOL-dark-card EOL-generate-inline" style={{marginBottom: 16}}>
            <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                <button 
            className="EOL-dark-generate" 
            onClick={() => {
              generateNewArt();
            }}
                  style={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                <Sparkles size={16} style={{marginRight: 8}} />
                Generate Artwork
                </button>
          
              {/* Render Queue Status — shared with mobile dock */}
              {generateQueueActive && (
                <GenerateQueueStatus
                  pendingCount={renderQueue.length}
                  currentJob={currentJob}
                  onCancelCurrent={cancelCurrentJob}
                  onClearQueue={clearRenderQueue}
                  previewStatus={previewStatusText}
                />
              )}
          
              {/* Quick Actions */}
              <div style={{display: 'flex', gap: 8}}>
                <button 
              onClick={isBulkGenerating ? stopBulkGeneration : () => generateMultipleArt(11)}
                  style={{
                    padding: '8px 12px',
                background: isBulkGenerating ? '#dc3545' : '#EB420E',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                cursor: 'pointer',
                fontSize: '0.9rem',
                flex: 1
                  }}
                >
                  {isBulkGenerating ? 'Stop' : 'x11'}
                </button>
                <button 
              onClick={isBulkGenerating ? stopBulkGeneration : () => generateMultipleArt(111)}
                  style={{
                    padding: '8px 12px',
                background: isBulkGenerating ? '#dc3545' : '#EB420E',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                cursor: 'pointer',
                fontSize: '0.9rem',
                flex: 1
                  }}
                >
                  {isBulkGenerating ? 'Stop' : 'x111'}
                </button>
              </div>
          

            </div>
              </div>

          {/* Core Art Settings */}
          <div className="EOL-dark-card" style={{marginBottom: 16}}>
            <h3 style={{margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#fff'}}>🎨 Art Settings</h3>
            


            {/* Palette Setting */}
            <div style={{marginBottom: 16}}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
                <label style={{fontSize: '0.9rem', color: '#ccc', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                  <Palette size={14} />
                  Palette
                </label>
              <button 
                  onClick={() => setRandomPaletteToggle(!randomPaletteToggle)}
                style={{
                    padding: '4px 8px',
                    background: randomPaletteToggle ? '#EB420E' : '#333',
                  color: '#fff',
                  border: 'none',
                    borderRadius: 4,
                  cursor: 'pointer',
                    fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}
              >
                  <Shuffle size={12} />
              </button>
            </div>
              <PaletteDropdown 
                palette={palette} 
                setPalette={setPalette} 
                groups={PALETTE_GROUPS} 
                getColors={name => PALETTE_COLORS[name]} 
                paletteColors={paletteColors} 
              />
            </div>

            {/* Color Limit Setting */}
            <div style={{marginBottom: 16}}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
                <label style={{fontSize: '0.9rem', color: '#ccc', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                  <Palette size={14} />
                  Color Limit
                </label>
              </div>
              <div style={{display: 'flex', gap: 8, marginBottom: 8}}>
                <button 
                  onClick={() => setColorLimitMode('off')}
                  style={{
                    padding: '6px 12px',
                    background: colorLimitMode === 'off' ? '#EB420E' : '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    flex: 1
                  }}
                >
                  Off
                </button>
                <button 
                  onClick={() => setColorLimitMode('auto')}
                  style={{
                    padding: '6px 12px',
                    background: colorLimitMode === 'auto' ? '#EB420E' : '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    flex: 1
                  }}
                >
                  Auto
                </button>
                <button 
                  onClick={() => setColorLimitMode('manual')}
                  style={{
                    padding: '6px 12px',
                    background: colorLimitMode === 'manual' ? '#EB420E' : '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    flex: 1
                  }}
                >
                  Manual
                </button>
              </div>
              {colorLimitMode === 'manual' && (
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                  <input 
                    type="range" 
                    min={COLOR_LIMIT_MIN} 
                    max={COLOR_LIMIT_MAX} 
                    step={1} 
                    value={colorLimit} 
                    onChange={e => setColorLimit(normalizeColorLimit(e.target.value))}
                    style={{flex: 1}}
                  />
                  <span style={{fontSize: '0.8rem', color: '#ccc', minWidth: '20px'}}>
                    {colorLimit}
                  </span>
                </div>
              )}
            </div>



            {/* Density Preset (replaces weave tightness slider) */}
            <div style={{marginBottom: 16}}>
              <label style={{fontSize: '0.9rem', color: '#ccc', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8}}>
                <Settings size={14} /> Pattern Density
              </label>
              <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap: 6}}>
                {[
                  {key:'random', label:'Auto'},
                  {key:'airy', label:'Airy'},
                  {key:'balanced', label:'Balanced'},
                  {key:'tight', label:'Tight'},
                ].map(({key,label}) => (
                  <button key={key}
                    onClick={()=>setDensityPreset(key as any)}
                    style={{padding:'6px 8px', border:'none', borderRadius:6, cursor:'pointer', background: densityPreset===key ? '#EB420E' : '#333', color:'#fff', fontWeight:600, fontSize:'0.8rem'}}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Diagonal Elements */}
            <div style={{marginBottom: 12}}>
              <div className="EOL-setting-feature-row">
                <div className="EOL-setting-feature-heading">
                  <span className="EOL-setting-feature-glyph" aria-hidden="true">
                    <Sparkles size={14} />
                  </span>
                  <span className="EOL-setting-feature-label">Diagonal Weaves</span>
                </div>
                <div
                  className="EOL-setting-feature-control"
                  role="switch"
                  aria-checked={diagonalMode}
                  aria-label="Diagonal Weaves"
                  tabIndex={0}
                  onClick={() => setDiagonalMode(!diagonalMode)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDiagonalMode(!diagonalMode);
                    }
                  }}
                  style={{
                    width: 44,
                    height: 24,
                    background: diagonalMode ? '#EB420E' : '#333',
                    borderRadius: 12,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    border: '1px solid #555'
                  }}
                >
                  <div style={{
                    width: 18,
                    height: 18,
                    background: '#fff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: 2,
                    left: diagonalMode ? 22 : 2,
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
              </div>
              <div style={{fontSize: '0.8rem', color: '#888'}}>Add diagonal elements for authentic madras look (processor heavy: browser freeze is normal, so wait for it to finish)</div>
            </div>

            {/* Cloth Flow (static drape) — enabled for Classic / Bleed / Modern */}
            <div style={{marginBottom: 12}}>
              <div className="EOL-setting-feature-row">
                <div className="EOL-setting-feature-heading">
                  <span className="EOL-setting-feature-glyph" aria-hidden="true">
                    <Sparkles size={14} />
                  </span>
                  <span className="EOL-setting-feature-label">
                    Cloth Flow (Static)
                  </span>
                </div>
                <div
                  className="EOL-setting-feature-control"
                  role="switch"
                  aria-checked={clothFlow}
                  aria-label="Cloth Flow (Static)"
                  tabIndex={0}
                  onClick={() => setClothFlow(!clothFlow)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setClothFlow(!clothFlow);
                    }
                  }}
                  style={{
                    width: 44,
                    height: 24,
                    background: clothFlow ? '#EB420E' : '#333',
                    borderRadius: 12,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    border: '1px solid #555'
                  }}
                >
                  <div style={{
                    width: 18,
                    height: 18,
                    background: '#fff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: 2,
                    left: clothFlow ? 22 : 2,
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
              </div>
              <div style={{fontSize: '0.8rem', color: '#888'}}>
                Adds realistic cloth drape by warping the finished pattern. Keeps color and weave intact.
              </div>
            </div>

            {/* Pattern Style */}
            <div style={{marginBottom: 12}}>
              <label style={{fontSize: '0.9rem', color: '#ccc', marginBottom: 8, display: 'block'}}>Pattern Style</label>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6}}>
                {[
                  { value: 'classic', label: 'Classic' },
                  { value: 'bleed', label: 'Bleed' },
                  { value: 'modern', label: 'Modern', dpr: '2.4x' },
                ].map(({ value, label, dpr }) => (
                  <button 
                    key={value}
                    onClick={() => setPatternType(value as any)}
                    style={{
                      padding: '6px 8px',
                      background: patternType === value ? '#EB420E' : '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      position: 'relative'
                    }}
                  >
                    <div>{label}</div>
                    {dpr && (
                      <div style={{
                        fontSize: '0.6rem',
                        opacity: 0.8,
                        marginTop: '2px'
                      }}>
                        {dpr}
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {patternType === 'modern' && (
                <div style={{fontSize: '0.8rem', color: '#888', marginTop: 6}}>
                  Modern patterns render at 2.4x resolution for extra crisp details
                </div>
              )}
            </div>

            {/* Background Color Setting */}
            <div style={{marginBottom: 16}}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
                <label style={{fontSize: '0.9rem', color: '#ccc', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                  <Palette size={14} />
                  Background Color
                </label>
              <button 
                onClick={() => setRandomBgColor(!randomBgColor)}
                style={{
                    padding: '4px 8px',
                  background: randomBgColor ? '#EB420E' : '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                    fontWeight: 'bold'
                }}
              >
                  <Shuffle size={12} />
              </button>
              </div>
              <BackgroundColorPicker bgColor={bgColor} setBgColor={setBgColor} opaque={opaque} setOpaque={setOpaque} />
            </div>

            {/* Lint Setting */}
            <div style={{marginBottom: 16}}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
                <label style={{fontSize: '0.9rem', color: '#ccc', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                  <Sparkles size={14} />
                  Fresh from Loom
                </label>
              <button 
                onClick={() => setRandomLint(!randomLint)}
                style={{
                    padding: '4px 8px',
                  background: randomLint ? '#EB420E' : '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}
                >
                  <Shuffle size={12} />
                </button>
              </div>
              <div 
                onClick={() => setLint(!lint)}
                style={{
                  width: 44,
                  height: 24,
                  background: lint ? '#EB420E' : '#333',
                  borderRadius: 12,
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  border: '1px solid #555'
                }}
              >
                <div style={{
                  width: 18,
                  height: 18,
                  background: '#fff',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: 2,
                  left: lint ? 22 : 2,
                  transition: 'left 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </div>
          </div>

          {/* Advanced Pattern Modes (removed; now consolidated under Art Settings) */}

          {/* Bitcoin Integration removed */}



          {/* Actions (removed; moved to gallery modals) */}
        </aside>
        {/* Canvas Area */}
        <main className="EOL-dark-canvas-area">
          <div
            ref={canvasContainerRef}
            className="EOL-dark-canvas-border"
            style={{
              position: 'relative',
              background: isFullscreen ? '#000' : undefined,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isFullscreen ? '100vh' : undefined,
              borderRadius: isFullscreen ? 0 : 12,
              boxShadow: isFullscreen ? 'none' : undefined,
              padding: isFullscreen ? 0 : undefined,
              margin: isFullscreen ? 0 : undefined,
              width: isFullscreen ? '100vw' : undefined,
              height: isFullscreen ? '100vh' : undefined,
              overflow: isFullscreen ? 'hidden' : undefined
            }}
          >
            {currentArt ? (
              (currentArt.displayUrl || currentArt.previewUrl) ? (
                <img
                  className="EOL-dark-canvas-media"
                  src={(currentArt.displayUrl || currentArt.previewUrl) as string}
                  alt={`Madras Check - Seed: ${currentArt.config.seed}`}
                  style={{
                    width: isFullscreen ? '100vw' : 1024,
                    height: isFullscreen ? '100vh' : 1024,
                    border: 'none',
                    borderRadius: isFullscreen ? 0 : 12,
                    background: 'transparent',
                    maxWidth: isFullscreen ? '100vw' : '100%',
                    maxHeight: isFullscreen ? '100vh' : '100%',
                    boxShadow: isFullscreen ? 'none' : undefined,
                    display: 'block',
                    margin: 0,
                    padding: 0,
                    objectFit: 'contain'
                  }}
                />
              ) : currentArt.previewPending ? (
                <div
                  className="EOL-dark-canvas-media"
                  style={{
                    width: isFullscreen ? '100vw' : 1024,
                    height: isFullscreen ? '100vh' : 1024,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isFullscreen ? 'transparent' : '#18191c',
                    borderRadius: isFullscreen ? 0 : 12,
                    color: '#888',
                    fontSize: '1.2rem',
                    maxWidth: isFullscreen ? '100vw' : '100%',
                    maxHeight: isFullscreen ? '100vh' : '100%',
                    boxShadow: isFullscreen ? 'none' : undefined,
                    margin: 0,
                    padding: 0
                  }}
                >
                  Rendering preview...
                </div>
              ) : (
                <iframe
                  className="EOL-dark-canvas-media"
                  title={`Madras Check - Seed: ${currentArt.config.seed}`}
                  srcDoc={currentArt.html}
                  style={{
                    width: isFullscreen ? '100vw' : 1024,
                    height: isFullscreen ? '100vh' : 1024,
                    border: 'none',
                    borderRadius: isFullscreen ? 0 : 12,
                    background: 'transparent',
                    maxWidth: isFullscreen ? '100vw' : '100%',
                    maxHeight: isFullscreen ? '100vh' : '100%',
                    boxShadow: isFullscreen ? 'none' : undefined,
                    display: 'block',
                    margin: 0,
                    padding: 0,
                    transition: 'none'
                  }}
                />
              )
            ) : (
              <div
                className="EOL-dark-canvas-media"
                style={{
                  width: isFullscreen ? '100vw' : 1024,
                  height: isFullscreen ? '100vh' : 1024,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isFullscreen ? 'transparent' : '#18191c',
                  borderRadius: isFullscreen ? 0 : 12,
                  color: '#888',
                  fontSize: '1.2rem',
                  maxWidth: isFullscreen ? '100vw' : '100%',
                  maxHeight: isFullscreen ? '100vh' : '100%',
                  boxShadow: isFullscreen ? 'none' : undefined,
                  margin: 0,
                  padding: 0
                }}
              >
                {currentJob || previewProgress.pending > 0
                  ? 'Generating artwork...'
                  : 'No artwork available'}
              </div>
            )}


            {/* Left Navigation Button */}
            {artQueue.length > 1 && !isFullscreen && (
              <button
                onClick={goToPrevious}
                disabled={currentArtIndex <= 0}
                style={{
                  position: 'absolute',
                  left: '-60px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '48px',
                  height: '48px',
                  background: currentArtIndex <= 0 ? 'rgba(51, 51, 51, 0.3)' : 'rgba(247, 147, 26, 0.9)',
                  border: '2px solid rgba(247, 147, 26, 0.5)',
                  borderRadius: '50%',
                  color: '#fff',
                  fontSize: '20px',
                  cursor: currentArtIndex <= 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  zIndex: 10,
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (currentArtIndex > 0) {
                    e.currentTarget.style.background = 'rgba(247, 147, 26, 1)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(247, 147, 26, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentArtIndex > 0) {
                    e.currentTarget.style.background = 'rgba(247, 147, 26, 0.9)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3)';
                  }
                }}
                title="Previous artwork"
                              >
                  <ChevronLeft size={20} />
                </button>
            )}

            {/* Right Navigation Button */}
            {artQueue.length > 1 && !isFullscreen && (
              <button
                onClick={goToNext}
                disabled={currentArtIndex >= artQueue.length - 1}
                style={{
                  position: 'absolute',
                  right: '-60px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '48px',
                  height: '48px',
                  background: currentArtIndex >= artQueue.length - 1 ? 'rgba(51, 51, 51, 0.3)' : 'rgba(247, 147, 26, 0.9)',
                  border: '2px solid rgba(247, 147, 26, 0.5)',
                  borderRadius: '50%',
                  color: '#fff',
                  fontSize: '20px',
                  cursor: currentArtIndex >= artQueue.length - 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  zIndex: 10,
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (currentArtIndex < artQueue.length - 1) {
                    e.currentTarget.style.background = 'rgba(247, 147, 26, 1)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(247, 147, 26, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentArtIndex < artQueue.length - 1) {
                    e.currentTarget.style.background = 'rgba(247, 147, 26, 0.9)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3)';
                  }
                }}
                title="Next artwork"
                              >
                  <ChevronRight size={20} />
                </button>
            )}

            {/* Queue Info & Latest Button */}
            {artQueue.length > 1 && !isFullscreen && (
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(35, 35, 35, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(247, 147, 26, 0.3)',
                borderRadius: '20px',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                zIndex: 10
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <BookOpen size={12} style={{color: '#EB420E'}} />
                  <span style={{fontSize: '0.8rem', color: '#fff', fontWeight: 500}}>
                    {displayedArtIndex + 1} of {artQueue.length}
                  </span>
                </div>
                <div style={{
                  width: '1px',
                  height: '16px',
                  background: 'rgba(255, 255, 255, 0.2)'
                }} />
                <button 
                  onClick={goToLatest}
                  disabled={currentArtIndex >= artQueue.length - 1}
                  style={{
                    padding: '4px 8px',
                    background: currentArtIndex >= artQueue.length - 1 ? 'rgba(51, 51, 51, 0.5)' : 'rgba(37, 99, 235, 0.9)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: currentArtIndex >= artQueue.length - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(5px)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentArtIndex < artQueue.length - 1) {
                      e.currentTarget.style.background = 'rgba(37, 99, 235, 1)';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentArtIndex < artQueue.length - 1) {
                      e.currentTarget.style.background = 'rgba(37, 99, 235, 0.9)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                  title="Go to latest artwork"
                >
                  Latest
                </button>
              </div>
            )}

            {/* Fullscreen Button */}
            <button
              onClick={toggleFullscreen}
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px',
                width: '48px',
                height: '48px',
                background: 'rgba(0, 0, 0, 0.7)',
                border: '2px solid #EB420E',
                borderRadius: '50%',
                color: '#EB420E',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(247, 147, 26, 0.2)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
          </div>
        </main>
      </div>





      {/* Unified Gallery Section */}
      {(artQueue.length > 0 || gallery.length > 0) && (
        <div className="EOL-gallery-section">
          {/* Gallery Navigation Bar — scrolls horizontally when actions exceed available width */}
          <div className="EOL-gallery-toolbar">
            <div className="EOL-gallery-toolbar-tabs">
              <button
                type="button"
                className={`EOL-gallery-toolbar-btn${galleryView === 'queue' ? ' is-active' : ''}`}
                onClick={() => setGalleryView('queue')}
              >
                Queue ({artQueue.length})
              </button>
              <button
                type="button"
                className={`EOL-gallery-toolbar-btn${galleryView === 'favorites' ? ' is-active' : ''}`}
                onClick={() => setGalleryView('favorites')}
              >
                Favorites ({gallery.length})
              </button>
            </div>

            <div className="EOL-gallery-toolbar-actions">
              {selectedGalleryItems.size > 0 ? (
                <>
                  <span className="EOL-gallery-toolbar-label">
                    Selected only {selectedGalleryItems.size}
                  </span>
                  <button
                    type="button"
                    className="EOL-gallery-toolbar-btn"
                    onClick={() => {
                      // Add selected items to favorites (save to gallery)
                      const selectedItems = getCurrentGalleryItems().filter(item => 
                        selectedGalleryItems.has(item.id)
                      );
                      selectedItems.forEach(item => {
                        if (!gallery.find(g => g.id === item.id)) {
                          const newGalleryItem = { 
                            id: item.id,
                            html: item.html,
                            config: item.config,
                            timestamp: item.timestamp,
                            seed, 
                            palette, 
                            lint, 
                            threadSpacing, 
                            bgColor, 
                            opaque,
                            artId: item.id 
                          };
                          setGallery(prev => {
                            const updated = [newGalleryItem, ...prev];
                            const persist = saveFavoritesToLocalStorage(updated);
                            if (!persist.ok) {
                              console.warn('Failed to persist favorites:', persist.reason);
                            }
                            return updated;
                          });
                        }
                      });
                      deselectAllGalleryItems();
                    }}
                  >
                    <Heart size={14} />
                    Favorite {selectedGalleryItems.size} items
                  </button>
                  <button
                    type="button"
                    className="EOL-gallery-toolbar-btn is-danger"
                    onClick={() => deleteGalleryItems(Array.from(selectedGalleryItems))}
                  >
                    <Trash2 size={14} />
                    Delete {selectedGalleryItems.size} items
                  </button>
                  <button
                    type="button"
                    className="EOL-gallery-toolbar-btn"
                    onClick={deselectAllGalleryItems}
                  >
                    <X size={14} />
                    Deselect all
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="EOL-gallery-toolbar-btn"
                  onClick={selectAllGalleryItems}
                >
                  <Settings size={14} />
                  Select all
                </button>
              )}
            </div>
          </div>

          {/* Gallery Grid — multi-row auto-fill (275px / 30px); row-windowed for x111 */}
          <VirtualizedGalleryStrip
            items={getCurrentGalleryItems()}
            getItemId={(item) => item.id}
            itemSize={GALLERY_CARD_SIZE}
            gap={GALLERY_CARD_GAP}
            overscanRows={2}
            renderItem={(item, index) => {
              const isSelected = selectedGalleryItems.has(item.id);
              const isQueueItem = galleryView === 'queue';

              return (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    border: isSelected ? '3px solid #fff' : '2px solid #333',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative',
                    background: '#1a1a1a',
                    transition: 'all 0.2s ease',
                    transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: isSelected ? '0 8px 25px rgba(255, 255, 255, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.4)';
                    const actionButtons = e.currentTarget.querySelector('[data-action-buttons]') as HTMLElement;
                    if (actionButtons) {
                      actionButtons.style.opacity = '1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = isSelected ? 'scale(1.05)' : 'scale(1)';
                    e.currentTarget.style.boxShadow = isSelected ? '0 8px 25px rgba(255, 255, 255, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.3)';
                    const actionButtons = e.currentTarget.querySelector('[data-action-buttons]') as HTMLElement;
                    if (actionButtons) {
                      actionButtons.style.opacity = '0';
                    }
                  }}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (e.ctrlKey || e.metaKey) {
                        toggleGalleryItemSelection(item.id);
                      } else {
                        clearPreviewFollow();
                        setModalArt(item);
                        if (isQueueItem) {
                          const queueIdx = artQueue.findIndex(a => a.id === item.id);
                          setCurrentArtIndex(queueIdx >= 0 ? queueIdx : currentArtIndex);
                          setModalIndex(queueIdx);
                        } else {
                          setModalIndex(getCurrentGalleryItems().findIndex(a => a.id === item.id));
                        }
                        updateURLWithConfig(item.config);
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt={`Gallery Artwork ${index + 1}`}
                        style={{
                          width: '1024px',
                          height: '1024px',
                          border: 'none',
                          transform: 'scale(0.269)',
                          transformOrigin: 'top left',
                          pointerEvents: 'none',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div
                        style={{
                          width: '1024px',
                          height: '1024px',
                          border: 'none',
                          transform: 'scale(0.269)',
                          transformOrigin: 'top left',
                          pointerEvents: 'none',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#888'
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <Loader2 className="spin" size={14} /> Generating preview...
                        </span>
                      </div>
                    )}
                  </div>

                  <div
                    data-action-buttons
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      zIndex: 10
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGalleryItemSelection(item.id);
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        background: selectedGalleryItems.has(item.id) ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.3)',
                        color: selectedGalleryItems.has(item.id) ? '#000' : '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontFamily: 'monospace',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = selectedGalleryItems.has(item.id) ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = selectedGalleryItems.has(item.id) ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.3)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      title={selectedGalleryItems.has(item.id) ? 'Deselect item' : 'Select item'}
                    >
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '1px solid currentColor',
                        borderRadius: '50%',
                        position: 'relative'
                      }}>
                        {selectedGalleryItems.has(item.id) && (
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '6px',
                            height: '6px',
                            background: 'currentColor',
                            borderRadius: '50%'
                          }} />
                        )}
                      </div>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!gallery.find(g => g.id === item.id)) {
                          const newGalleryItem = {
                            id: item.id,
                            html: item.html,
                            config: item.config,
                            timestamp: item.timestamp,
                            seed,
                            palette,
                            lint,
                            threadSpacing,
                            bgColor,
                            opaque,
                            patternType,
                            artId: item.id
                          };
                          setGallery(prev => {
                            const updated = [newGalleryItem, ...prev];
                            const persist = saveFavoritesToLocalStorage(updated);
                            if (!persist.ok) {
                              console.warn('Failed to persist favorites:', persist.reason);
                            }
                            return updated;
                          });
                        }
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        background: gallery.find(g => g.id === item.id) ? 'rgba(247, 147, 26, 0.8)' : 'rgba(0, 0, 0, 0.3)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontFamily: 'monospace',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = gallery.find(g => g.id === item.id) ? 'rgba(247, 147, 26, 1)' : 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = gallery.find(g => g.id === item.id) ? 'rgba(247, 147, 26, 0.8)' : 'rgba(0, 0, 0, 0.3)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      title={gallery.find(g => g.id === item.id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart size={16} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isQueueItem) {
                          removeArtQueueItems([item.id]);
                        } else {
                          deleteGalleryItems([item.id]);
                        }
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        background: 'rgba(220, 38, 38, 0.3)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontFamily: 'monospace',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(220, 38, 38, 0.8)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(220, 38, 38, 0.3)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      title="Delete item"
                    >
                      <Trash2 size={16} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.config) {
                          setSeed(item.config.seed || '');
                          const paletteLabel = getDisplayPaletteName(item.config);
                          setPalette(paletteLabel === 'Custom'
                            ? (item.config.palette || '')
                            : paletteLabel);
                          setPaletteColors(item.config.paletteColors || null);
                          setLint(item.config.lint || '');
                          setThreadSpacing(item.config.threadSpacing || '');
                          setBgColor(item.config.bgColor || '#fff');
                          setOpaque(item.config.opaque || false);
                        }
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontFamily: 'monospace',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      title="Use traits"
                    >
                      <Settings size={16} />
                    </button>
                  </div>

                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: '#EB420E',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                  }}>
                    {index + 1}
                  </div>
                </div>
              );
            }}
          />
        </div>
      )}

      {modalArt && (
        <div
          className="EOL-preview-overlay"
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div
            className="EOL-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Artwork preview"
          >
            {/* Artwork */}
            <div className="EOL-preview-art">
              {/* Navigation Arrows */}
              {modalIndex > 0 && (
                <button
                  type="button"
                  className="EOL-preview-nav is-prev"
                  onClick={goToPreviousModal}
                  aria-label="Previous artwork"
                  style={{
                    position: 'absolute',
                    left: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    fontSize: 20,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0,0,0,0.9)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                  }}
                >
                  ←
                </button>
              )}
              
              {modalIndex < artQueue.length - 1 && (
                <button
                  type="button"
                  className="EOL-preview-nav is-next"
                  onClick={goToNextModal}
                  aria-label="Next artwork"
                  style={{
                    position: 'absolute',
                    right: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    fontSize: 20,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0,0,0,0.9)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                  }}
                >
                  →
                </button>
              )}

              <div className="EOL-preview-art-frame">
                {(modalArt.displayUrl || modalArt.previewUrl) ? (
                  <img
                    className="EOL-preview-art-media"
                    src={(modalArt.displayUrl || modalArt.previewUrl) as string}
                    alt={`Madras Check - Seed: ${modalArt.config.seed}`}
                  />
                ) : modalArt.previewPending ? (
                  <div
                    className="EOL-preview-art-media"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#888',
                      fontSize: '1.1rem',
                      background: '#18191c'
                    }}
                  >
                    Rendering preview...
                  </div>
                ) : (
                  <iframe
                    className="EOL-preview-art-media"
                    srcDoc={modalArt.html}
                    title={`Madras Check - Seed: ${modalArt.config.seed}`}
                  />
                )}
              </div>
            </div>
            {/* Specs Sidebar */}
            <div className="EOL-preview-specs">
              <h2 style={{fontSize: 22, fontWeight: 700, marginBottom: 16}}>Specs</h2>
              <div style={{marginBottom: 12}}><b>Seed:</b> <span style={{fontFamily: 'monospace'}}>{modalArt.config.seed}</span></div>
              <div style={{marginBottom: 12}}><b>Palette:</b> {getDisplayPaletteName(modalArt.config)}
                <div style={{marginTop: 4}}>
                  <PaletteSwatch colors={getDisplayPaletteColors(modalArt.config)} showHexTooltips />
                </div>
              </div>
              <div style={{marginBottom: 12}}><b>Thread Spacing:</b> {formatThreadSpacing(modalArt.config.threadSpacing)}</div>
              <div style={{marginBottom: 12}}><b>Background:</b> <span style={{background: modalArt.config.bgColor, color: '#000', padding: '2px 8px', borderRadius: 4}}>{modalArt.config.bgColor}</span></div>
              
              <div style={{marginBottom: 12}}><b>Pattern Style:</b> {modalArt.config.patternType || 'classic'}</div>
              <div style={{marginBottom: 12}}><b>Lint:</b> {modalArt.config.lint ? 'Yes' : 'No'}</div>
              <div style={{marginBottom: 12}}><b>Opaque:</b> {modalArt.config.opaque ? 'Yes' : 'No'}</div>
              {/* Advanced Pattern Modes removed from modal */}
              
              {/* Bitcoin traits removed */}
              {/* Modal actions */}
              <div style={{marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12}}>
                <button
                  onClick={() => {
                    const id = encodeConfigToPermalinkId(modalArt.config);
                    const url = buildSharePermalink(id);
                    updateURLWithConfig(modalArt.config);
                    navigator.clipboard.writeText(url);
                    alert('Permalink copied!');
                  }}
                  style={{padding: '12px 16px', background: '#EB420E', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer'}}
                >Copy Permalink</button>

                <button
                  onClick={async () => {
                    try {
                      const id = encodeConfigToPermalinkId(modalArt.config);
                      const permalink = buildSharePermalink(id);
                      const postText = buildArtworkPostText({
                        seed: String(modalArt.config.seed || ''),
                        paletteName: getDisplayPaletteName(modalArt.config),
                        patternType: String(modalArt.config.patternType || 'classic'),
                        permalink,
                      });
                      await navigator.clipboard.writeText(postText);
                      alert('Post copied');
                    } catch (err) {
                      console.warn('Copy Post failed', err);
                      alert('Could not copy post. Please try again.');
                    }
                  }}
                  style={{padding: '12px 16px', background: '#EB420E', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer'}}
                >Copy Post</button>

                <button
                  onClick={() => {
                    const newGalleryItem = { 
                      id: modalArt.id,
                      html: modalArt.html,
                      config: modalArt.config,
                      timestamp: modalArt.timestamp,
                      seed: modalArt.config.seed, 
                      palette: modalArt.config.palette, 
                      lint: modalArt.config.lint, 
                      threadSpacing: modalArt.config.threadSpacing, 
                      bgColor: modalArt.config.bgColor, 
                      opaque: modalArt.config.opaque,
                      artId: modalArt.id 
                    };
                    const updatedGallery = [...gallery, newGalleryItem];
                    const persist = saveFavoritesToLocalStorage(updatedGallery);
                    if (!persist.ok) {
                      console.warn('Failed to persist favorites:', persist.reason);
                      alert(
                        persist.reason === 'quota'
                          ? 'Favorites storage is full. Remove some favorites and try again.'
                          : 'Could not save favorite. Please try again.'
                      );
                      return;
                    }
                    setGallery(updatedGallery);
                    alert('Added to favorites!');
                  }}
                  style={{padding: '12px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer'}}
                >💖 Add to Favorites</button>

                <button
                  onClick={() => setShowExportModal(true)}
                  style={{padding: '12px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer'}}
                >📥 Export Image</button>

                <button
                  onClick={() => {
                    setSeed(modalArt.config.seed);
                    setPalette(getDisplayPaletteName(modalArt.config) === 'Custom'
                      ? modalArt.config.palette
                      : getDisplayPaletteName(modalArt.config));
                    setPaletteColors(modalArt.config.paletteColors || null);
                    // Carry the previewed artwork's resolved seeded-palette colors
                    // into this one generation only (Phase 6J / 6K).
                    carriedPaletteColorsRef.current =
                      isSeededPaletteMode(getDisplayPaletteName(modalArt.config))
                        ? reusablePaletteColors(modalArt.config.paletteColors)
                        : null;
                    setThreadSpacing(modalArt.config.threadSpacing);
                    setBgColor(modalArt.config.bgColor);
                    setLint(modalArt.config.lint);
                    setOpaque(modalArt.config.opaque);
                    setModalArt(null);
                    setPendingGeneration(true);
                  }}
                  style={{padding: '12px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer'}}
                >Create with these traits</button>
              </div>
            </div>
            {/* Close button */}
            <button
              type="button"
              className="EOL-preview-close"
              onClick={() => closeModal()}
              title="Close"
              aria-label="Close preview"
            >×</button>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: 12,
            padding: 24,
            maxWidth: 500,
            width: '90%',
            border: '1px solid #333',
            position: 'relative'
          }}>
            <h3 style={{margin: '0 0 20px 0', color: '#fff', fontSize: '1.2rem', fontWeight: 600}}>
              Export Image
            </h3>
            
            {/* Canvas Size Selection */}
            <div style={{marginBottom: 20}}>
              <label style={{display: 'block', marginBottom: 8, color: '#ccc', fontSize: '0.9rem'}}>
                Canvas Size:
              </label>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8}}>
                {CANVAS_SIZES.map((size) => (
                  <button
                    key={size.value}
                    onClick={() => setExportCanvasSize(size.value)}
                    style={{
                      padding: '12px 16px',
                      background: exportCanvasSize === size.value ? '#EB420E' : '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transition: 'background 0.2s ease'
                    }}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Export Format Selection */}
            <div style={{marginBottom: 20}}>
              <label style={{display: 'block', marginBottom: 8, color: '#ccc', fontSize: '0.9rem'}}>
                Export Format:
              </label>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8}}>
                {EXPORT_FORMATS.map((format) => (
                  <button
                    key={format.value}
                    onClick={() => setExportFormat(format.value)}
                    style={{
                      padding: '12px 16px',
                      background: exportFormat === format.value ? '#EB420E' : '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    <span>{format.label}</span>
                    <span style={{fontSize: '0.7rem', opacity: 0.7}}>{format.description}</span>
                  </button>
                ))}
              </div>
            </div>



            {/* Export Button */}
            <div style={{marginBottom: 20}}>
              <button
                onClick={() => {
                  const art = modalArt || currentArt;
                  if (art) {
                    if (exportFormat === 'png') {
                      exportAsPNG(art, exportCanvasSize);
                    } else if (exportFormat === 'tiff') {
                      exportAsTIFF(art, exportCanvasSize);
                    } else if (exportFormat === 'jpeg') {
                      exportAsJPEG(art, exportCanvasSize);
                    } else if (exportFormat === 'webp') {
                      exportAsWebP(art, exportCanvasSize);
                    }
                    closeExportModal();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: '#EB420E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e64a19';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#EB420E';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Export as {exportFormat.toUpperCase()}
              </button>
            </div>

            {/* Close Button */}
            <button
              onClick={closeExportModal}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                 height: 32,
                fontSize: 18,
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}


      {/* Mobile generate dock — twin of inline controls; shown only ≤900px via CSS */}
      <nav
        className={`EOL-generate-dock${generateQueueActive ? ' is-queue-active' : ''}`}
        aria-label="Generate artwork"
      >
        {generateQueueActive && (
          <GenerateQueueStatus
            pendingCount={renderQueue.length}
            currentJob={currentJob}
            onCancelCurrent={cancelCurrentJob}
            onClearQueue={clearRenderQueue}
            previewStatus={previewStatusText}
          />
        )}
        <div className="EOL-generate-dock-actions">
          <button
            type="button"
            className="EOL-dark-generate EOL-generate-dock-primary"
            onClick={() => {
              generateNewArt();
            }}
          >
            <Sparkles size={16} aria-hidden="true" />
            Generate Artwork
          </button>
          <button
            type="button"
            className={`EOL-generate-dock-bulk${isBulkGenerating ? ' is-stop' : ''}`}
            onClick={isBulkGenerating ? stopBulkGeneration : () => generateMultipleArt(11)}
          >
            {isBulkGenerating ? 'Stop' : 'x11'}
          </button>
          <button
            type="button"
            className={`EOL-generate-dock-bulk${isBulkGenerating ? ' is-stop' : ''}`}
            onClick={isBulkGenerating ? stopBulkGeneration : () => generateMultipleArt(111)}
          >
            {isBulkGenerating ? 'Stop' : 'x111'}
          </button>
        </div>
      </nav>

    </div>
  );
}
